import React, { useState, useEffect, useLayoutEffect, useRef, useCallback, useMemo } from 'react';
import { flushSync } from 'react-dom';
import { X, Home, Pencil, Package, Truck, DollarSign, Wrench, Hammer, Car, Trash2, Plus, Factory, Copy, Image as ImageIcon, FileText, Download, ChevronDown, Info, SlidersHorizontal, Camera, AlertTriangle, TreePine } from 'lucide-react';
import { ProposalNodeSet, TimberListItem, SpeciesTimberConfig, MediaItem, GlobalCosts, DeckingItem, DesignNodeData, HardwareListItem, PostBasesItem, DesignEstimatingData } from '../../types';
import {
    deckingItemTotal,
    getStructureDeckingItems,
    resolveDeckingWidthInches,
} from '../../utils/proposalStructureDecking';
import { getStructurePostBasesItems, withPostBasesRollups } from '../../utils/proposalStructurePostBases';
import { ProposalRateSettings } from '../../utils/appSettings';
import {
    subscribeToTimberItems,
    TimberItem,
    subscribeToHardwareItems,
    HardwareItem,
    subscribeToGlulamItems,
    GlulamItem,
    findTimberCatalogMatch,
    gradesEquivalent,
    canonicalizeGrade,
} from '../../utils/materialsDatabaseService';
import {
    buildProposalTimberSpeciesOptions,
    findGlulamCatalogMatchForProposal,
    findProposalTimberPiecePrice,
    gradesForProposalTimberSpecies,
} from '../../utils/proposalTimberCatalog';
import { syncNodeSetCatalogPrices } from '../../utils/proposalCatalogPriceSync';
import { computeInstallNodeRawTotal, getEffectiveProjectOverheadPercent, countGlobalCostShareEligible } from '../../utils/proposalCalculations';
import { stampStructureDesignTotal } from '../../utils/proposalDesignBreakdown';
import DesignBreakdownGroups from './DesignBreakdownGroups';
import InstallBreakdownGroups from './InstallBreakdownGroups';
import ProductionBreakdownGroups from './ProductionBreakdownGroups';
import TravelBreakdownGroups from './TravelBreakdownGroups';
import {
    computeTravelNodeRawTotal,
    normalizeTravelTotalOnLoad,
    withDefaultHotelNights,
} from '../../utils/proposalTravelTotals';
import { getInstallLaborLines } from '../../utils/proposalInstallLabor';
import { timberLineHasOrderLengthUnder8Feet } from '../../utils/timberListValidation';
import { abbreviateSpeciesName, abbreviateTimberDrynessDisplay, supplierInitialsFromName } from '../../utils/timberAbbreviations';
import {
  openTimberPrintWindow,
  printTimberSupplierToWindow,
  TIMBER_SUPPLIER_POPUP_BLOCKED_MSG,
  timberSupplierExportHasContent,
} from '../../utils/supplierTimberExport';
import { useAuth } from '../../utils/AuthContext';
import { useOverflowWheelScroll } from '../../hooks/useOverflowWheelScroll';
import StructureTimberVersionsPanel from './StructureTimberVersionsPanel';
import QuickAddTimberDialog from './QuickAddTimberDialog';
import { canQuickAddTimberEstimate } from '../../utils/timberQuickAddEstimate';
import { uploadMediaItem, getMediaType } from '../../utils/mediaLibrary';
import { MediaPicker } from '../media';
import BoardSnapshotPicker, {
    type BoardSnapshotModelChoice,
    type BoardSnapshotPdfChoice,
} from '../board/BoardSnapshotPicker';
import HtmlViewerModal from '../html/HtmlViewerModal';
import { renderPdfPageToFile } from '../../utils/boardSnapshotCapture';
import type { ProposalOpsReviewComment, ProposalOpsReviewNodeType } from '../../types';
import OpsReviewStructureCommentsPopout from './OpsReviewStructureCommentsPopout';
import { enabledProposalStructures } from '../../utils/proposalStructureRefs';
import type { StructureMentionProjectOption } from '../../utils/structureMentionProjectOptions';
import { filterStructureCommentsByNodeType, getCommentsForStructure } from '../../utils/proposalOpsReview';
import {
    applyTimberDefaultsToTimberList,
    inferTimberDefaultsFromList,
    type ProposalTimberDefaults,
} from '../../utils/proposalTimberDefaults';
import { hardwareListTotal, syncHardwareRollups } from '../../utils/proposalHardwareCsvParser';
import { copyNodeFromStructure, type CopyableNodeKind } from '../../utils/proposalCopyNodeValues';
import HardwareListTable from './HardwareListTable';
import {
    compareStructureLineItems,
    formatStructureLineCompareTitle,
    structureLineCompareRowClass,
} from '../../utils/structureLineCompare';
import { speciesTimberKitRawDelta } from '../../utils/proposalSpeciesProduction';
import {
    clearProductionBoardFeetOverride,
    enableProductionBoardFeetOverride,
    getMaterialsTotalBoardFeet,
    isProductionBoardFeetOverridden,
    normalizeProductionBoardFeetOnLoad,
    resolveProductionBoardFeet,
    setProductionBoardFeetOverride,
    syncProductionBoardFeetFromMaterials,
} from '../../utils/productionBoardFeet';
import { useProposalStructurePresence } from '../../hooks/useProposalStructurePresence';
import ProposalStructurePresenceLayer, {
    ProposalStructurePresenceBadges,
} from './ProposalStructurePresenceLayer';
import './StructureDetailModal.css';
import './ProductionBoardFeetOverride.css';
import './ProposalOpsReview.css';

// Check if a timber has an odd (non-even) width or height
const hasOddDimension = (dimensions: string): boolean => {
    const match = dimensions.match(/(\d+\.?\d*)\s*[xX×]\s*(\d+\.?\d*)\s*[xX×]\s*(\d+\.?\d*)/);
    if (!match) return false;
    const height = parseFloat(match[1]) || 0;
    const width = parseFloat(match[2]) || 0;
    if (height === 0 && width === 0) return false;
    return height % 2 !== 0 || width % 2 !== 0;
};

interface StructureDetailModalProps {
    nodeSet: ProposalNodeSet;
    /** Proposal deal id — required for timber list versioning. */
    dealId?: string;
    allNodeSets: ProposalNodeSet[];  // All structures for project-wide export
    /** Shown on supplier RFQ PDF header */
    exportDealTitle?: string;
    rates: ProposalRateSettings;
    profitMargin: number;
    onClose: () => void;
    onUpdate: (updates: Partial<ProposalNodeSet>) => void;
    /** Joinery / engineering stay editable after send. */
    allowPostSendDesignEdits?: boolean;
    onDelete: () => void;
    onRename: (newName: string) => void;
    hideInstallTravel?: boolean;  // Hide install/travel sections (for kit builder)
    kitMode?: boolean;  // Kit proposal mode - hide Design/Materials/Production/Freight (only Install/Travel editable)
    disabled?: boolean;  // Proposal is sent - all fields read-only
    /** When project overhead (% of total) is enabled in Global Project Costs, per-structure Overhead days are locked. */
    globalCosts?: GlobalCosts | null;
    /**
     * Hide per-structure Freight and Overhead editors. Proposal builder always
     * hides them (freight is a Global Project Cost; overhead is the global %).
     * Kit builder still shows them. Calculation still includes any leftover
     * node freight/overhead so existing proposals do not change totals.
     */
    hideStructureFreightAndOverhead?: boolean;
    opsReviewPending?: boolean;
    /** True when OPS review is pending, approved, or has comments on this proposal. */
    opsReviewActive?: boolean;
    opsReviewComments?: ProposalOpsReviewComment[];
    canOpsReviewComment?: boolean;
    onAddOpsReviewComment?: (
        message: string,
        nodeType: ProposalOpsReviewNodeType,
        nodeLabel: string
    ) => Promise<void>;
    projectSharePercent?: number | null;
    /** Last species / grade / dryness set on another structure this session. */
    recommendedTimberDefaults?: ProposalTimberDefaults | null;
    onRememberTimberDefaults?: (defaults: ProposalTimberDefaults) => void;
    /** When true, apply recommended defaults once when this structure opens. */
    shouldAutoApplyTimberDefaults?: boolean;
    onTimberDefaultsAutoApplied?: (structureId: string) => void;
    /** Linked TimberProject id — enables capture from project PDFs / 3D models. */
    projectId?: string | null;
    /** Open Tools → Timber Input, locked to this structure. */
    onAddTimber?: () => void;
    /** Fill a compare-split column instead of the fullscreen overlay. */
    layout?: 'fullscreen' | 'pane';
    /** Other structure for line-item compare highlights. */
    compareOtherNodeSet?: ProposalNodeSet | null;
    /** Extra header label (e.g. remote proposal title). */
    comparePaneLabel?: string;
    /** Included in loaded $/sf (global costs + estimating). */
    designEstimating?: DesignEstimatingData | null;
    proposalStatus?: string | null;
    /** For / structure mentions in OPS review comments (project then structure). */
    structureMentionProjectOptions?: StructureMentionProjectOption[];
}

const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
    }).format(value);
};

function CopyNodeFromSelect({
    structures,
    currentId,
    disabled,
    onPick,
}: {
    structures: ProposalNodeSet[];
    currentId: string;
    disabled?: boolean;
    onPick: (sourceId: string) => void;
}) {
    const others = structures.filter((s) => s.id !== currentId);
    if (disabled || others.length === 0) return null;
    return (
        <select
            className="copy-node-from"
            value=""
            title="Copy this section from another structure"
            aria-label="Copy this section from another structure"
            onChange={(e) => {
                const id = e.target.value;
                if (id) onPick(id);
            }}
        >
            <option value="">Copy…</option>
            {others.map((s) => (
                <option key={s.id} value={s.id}>{s.name}</option>
            ))}
        </select>
    );
}

/** Same cap as MediaUploader for structure photo drops */
const STRUCTURE_IMAGE_MAX_BYTES = 100 * 1024 * 1024;

// Parse dimension string (HxWxL where H=height in inches, W=width in inches, L=length in feet)
// Returns board feet: (H * W * L) / 12
const parseDimensions = (dimensions: string): number => {
    const match = dimensions.match(/(\d+\.?\d*)\s*[xX×]\s*(\d+\.?\d*)\s*[xX×]\s*(\d+\.?\d*)/);
    if (!match) return 0;
    const height = parseFloat(match[1]); // inches
    const width = parseFloat(match[2]);  // inches  
    const length = parseFloat(match[3]); // feet
    // Board feet formula: (thickness_in × width_in × length_ft) / 12
    return (height * width * length) / 12;
};

const StructureDetailModal: React.FC<StructureDetailModalProps> = ({
    nodeSet,
    dealId,
    allNodeSets,
    exportDealTitle,
    rates,
    profitMargin,
    onClose,
    onUpdate,
    allowPostSendDesignEdits = false,
    onDelete,
    onRename,
    hideInstallTravel = false,
    kitMode = false,
    disabled = false,
    globalCosts,
    hideStructureFreightAndOverhead = true,
    opsReviewPending = false,
    opsReviewActive = false,
    opsReviewComments = [],
    canOpsReviewComment = false,
    onAddOpsReviewComment,
    projectSharePercent = null,
    recommendedTimberDefaults = null,
    onRememberTimberDefaults,
    shouldAutoApplyTimberDefaults = false,
    onTimberDefaultsAutoApplied,
    projectId = null,
    onAddTimber,
    layout = 'fullscreen',
    compareOtherNodeSet = null,
    comparePaneLabel,
    designEstimating = null,
    proposalStatus = null,
    structureMentionProjectOptions = [],
}) => {
    const structureOverheadLockedByProjectOverhead =
        !disabled && getEffectiveProjectOverheadPercent(globalCosts) > 0;

    const structureOpsComments = useMemo(
        () => getCommentsForStructure(opsReviewComments, nodeSet.id, nodeSet.name),
        [opsReviewComments, nodeSet.id, nodeSet.name]
    );

    // Local state for editing
    const seedMaterials = (ns: ProposalNodeSet): ProposalNodeSet => ({
        ...ns,
        materials: syncHardwareRollups(ns.materials),
    });
    const prepareNodeSetForModal = useCallback(
        (ns: ProposalNodeSet): ProposalNodeSet =>
            normalizeTravelTotalOnLoad(seedMaterials(normalizeProductionBoardFeetOnLoad(ns)), rates),
        [rates],
    );
    const [localNodeSet, setLocalNodeSet] = useState(() => prepareNodeSetForModal(nodeSet));
    const localNodeSetRef = useRef(prepareNodeSetForModal(nodeSet));
    const lastParentTimberIdsRef = useRef(
        (nodeSet.materials?.timberList ?? []).map(item => item.id).join('\0'),
    );
    const timberListScrollRef = useRef<HTMLDivElement>(null);
    useLayoutEffect(() => {
        localNodeSetRef.current = localNodeSet;
    }, [localNodeSet]);

    /** Push structure edits to parent immediately so timber species/grade/dryness survive modal close. */
    const commitLocalNodeSet = useCallback(
        (next: ProposalNodeSet) => {
            localNodeSetRef.current = next;
            setLocalNodeSet(next);
            onUpdate(next);
        },
        [onUpdate],
    );

    // Stale travel.total (e.g. fuelBudget set but total left at 0) breaks structure footer vs breakdown rows.
    useEffect(() => {
        if (disabled) return;
        const synced = prepareNodeSetForModal(nodeSet);
        if (Math.abs((synced.travel.total || 0) - (nodeSet.travel.total || 0)) < 0.01) return;
        commitLocalNodeSet(synced);
        // eslint-disable-next-line react-hooks/exhaustive-deps -- sync once per open when parent travel.total is stale
    }, [disabled, nodeSet.id, prepareNodeSetForModal, commitLocalNodeSet]);
    const [editingName, setEditingName] = useState(false);
    const [nameValue, setNameValue] = useState(nodeSet.name);

    // Local rate overrides. Source of truth for $/bdft is now the
    // persisted `production.ratePerBdft` field — exact, no float drift.
    // For legacy structures (saved before that field existed) we fall back
    // to the implied rate `total / boardFeet`; if that's also unusable
    // (empty structure, total=0) we use the global default.
    const [localRates, setLocalRates] = useState(() => {
        const bf = nodeSet.production.boardFeet;
        const total = nodeSet.production.total;
        const persistedRate = nodeSet.production.ratePerBdft;
        // A persisted rate of 0 is a valid, intentional value (user zeroed the
        // node) — only treat the field as "unset" when it's undefined/NaN, so we
        // don't clobber an explicit 0 with the global default on reopen.
        const impliedCnc =
            typeof persistedRate === 'number' && Number.isFinite(persistedRate) && persistedRate >= 0
                ? persistedRate
                : bf > 0 && total > 0 && Number.isFinite(total / bf)
                    ? total / bf
                    : rates.productionRatePerBdft;
        return {
            pmRate: rates.pmRate,
            apmRate: rates.apmRate ?? 40,
            leadDesignerRate: rates.leadDesignerRate,
            supportDesignerRate: rates.supportDesignerRate,
            engineeringRate: rates.engineeringRate,
            productionRatePerBdft: impliedCnc,
        };
    });

    // Freight mode state
    const [freightFlatFeeMode, setFreightFlatFeeMode] = useState({
        toProduction: localNodeSet.freight.flatFeeToAspers > 0,
        toJobSite: localNodeSet.freight.flatFeeToJobSite > 0,
    });

    // Materials Database state
    const [timberDatabase, setTimberDatabase] = useState<TimberItem[]>([]);
    const [glulamDatabase, setGlulamDatabase] = useState<GlulamItem[]>([]);
    const [hardwareDatabase, setHardwareDatabase] = useState<HardwareItem[]>([]);
    const deckingProducts = useMemo(
        () => hardwareDatabase.filter(item => item.category === 'Decking'),
        [hardwareDatabase],
    );
    const postBaseProducts = useMemo(
        () => hardwareDatabase
            .filter(item => item.category === 'Post Bases')
            .sort((a, b) => a.name.localeCompare(b.name)),
        [hardwareDatabase],
    );

    // Multi-species state
    const [activeSpeciesTab, setActiveSpeciesTab] = useState<string>('default'); // 'default' or species name
    const [showAddSpeciesModal, setShowAddSpeciesModal] = useState(false);
    const [showBulkTimberAttrsModal, setShowBulkTimberAttrsModal] = useState(false);
    const [bulkTimberSpecies, setBulkTimberSpecies] = useState('');
    const [bulkTimberGrade, setBulkTimberGrade] = useState('');
    const [bulkTimberMoisture, setBulkTimberMoisture] = useState<'' | 'green' | 'kiln_dried'>('');

    const { user } = useAuth();
    const structurePresence = useProposalStructurePresence(dealId, nodeSet.id);
    const structureGalleryRef = useRef<HTMLDivElement>(null);
    const [structureGalleryDragOver, setStructureGalleryDragOver] = useState(false);
    const [structureGalleryUploading, setStructureGalleryUploading] = useState(false);

    // Image picker state
    const [showMediaPicker, setShowMediaPicker] = useState(false);
    const [snapshotPickerOpen, setSnapshotPickerOpen] = useState(false);
    const [snapshotImporting, setSnapshotImporting] = useState(false);
    const [snapshotImportProgress, setSnapshotImportProgress] = useState<{ current: number; total: number } | null>(null);
    const [htmlCaptureDoc, setHtmlCaptureDoc] = useState<BoardSnapshotModelChoice | null>(null);
    const htmlCaptureCountRef = useRef(0);
    const [newSpeciesName, setNewSpeciesName] = useState('');
    const [altProductionRateDrafts, setAltProductionRateDrafts] = useState<Record<string, string>>({});
    const [showToolsMenu, setShowToolsMenu] = useState(false);
    const [quickAddTimberItem, setQuickAddTimberItem] = useState<TimberListItem | null>(null);
    const productionBoardFeetInputRef = useRef<HTMLInputElement>(null);
    const productionBoardFeetGroupRef = useRef<HTMLDivElement>(null);
    const productionBoardFeetFlashTimerRef = useRef<number | null>(null);

    // Lightbox state
    const [lightboxOpen, setLightboxOpen] = useState(false);
    const [lightboxIndex, setLightboxIndex] = useState(0);
    const lightboxOverlayRef = useRef<HTMLDivElement>(null);

    // Subscribe to Materials Database
    useEffect(() => {
        const unsubTimber = subscribeToTimberItems(
            (items) => setTimberDatabase(items),
            (error) => console.error('[StructureDetailModal] Timber DB error:', error)
        );
        const unsubGlulam = subscribeToGlulamItems(
            (items) => setGlulamDatabase(items),
            (error) => console.error('[StructureDetailModal] Glulam DB error:', error),
        );
        const unsubHardware = subscribeToHardwareItems(
            (items) => setHardwareDatabase(items),
            (error) => console.error('[StructureDetailModal] Hardware DB error:', error)
        );
        return () => { unsubTimber(); unsubGlulam(); unsubHardware(); };
    }, []);

    const autoApplyTimberDefaultsAttemptedRef = useRef(false);

    const speciesOptions = useMemo(
        () =>
            buildProposalTimberSpeciesOptions(
                timberDatabase,
                glulamDatabase,
                [
                    ...localNodeSet.materials.timberList.map((item) => item.species),
                    ...(localNodeSet.materials.alternateSpecies || []).flatMap((alt) =>
                        alt.timberList.map((item) => item.species),
                    ),
                ],
            ),
        [timberDatabase, glulamDatabase, localNodeSet.materials.timberList, localNodeSet.materials.alternateSpecies],
    );

    const getGradesForSpecies = (species: string, currentGrade?: string): string[] =>
        gradesForProposalTimberSpecies(timberDatabase, glulamDatabase, species, currentGrade);

    const getSupplierInitials = (species: string, grade: string, dimensions: string, moistureRaw?: string): string => {
        const glulamMatch = findGlulamCatalogMatchForProposal(glulamDatabase, species, grade, dimensions);
        const timberMatch = glulamMatch
            ? undefined
            : findTimberCatalogMatch(timberDatabase, species, grade, dimensions, moistureRaw);
        return supplierInitialsFromName((glulamMatch ?? timberMatch)?.supplier);
    };

    const getPiecePrice = (species: string, grade: string, dimensions: string, moistureRaw?: string): number =>
        findProposalTimberPiecePrice(timberDatabase, glulamDatabase, species, grade, dimensions, moistureRaw);

    // NOTE: We intentionally do NOT sync nodeSet prop → localNodeSet via useEffect.
    // The modal already initializes from props via useState(nodeSet), and handleNotesChange/
    // handleBlur keep parent + local in sync on every change. A useEffect here would
    // overwrite the textarea mid-typing when saves trigger parent re-renders.

    // Auto-correct timber grades when database loads (fix stale grade values)
    useEffect(() => {
        if (disabled || (timberDatabase.length === 0 && glulamDatabase.length === 0)) return;

        setLocalNodeSet(prev => {
            let needsUpdate = false;

            const correctGrades = (timberList: typeof prev.materials.timberList) =>
                timberList.map(item => {
                    const availableGrades = getGradesForSpecies(item.species, item.grade);
                    if (availableGrades.length === 0) return item;
                    const matchedGrade = availableGrades.find(g => gradesEquivalent(g, item.grade));
                    if (!matchedGrade) {
                        // Keep the line's grade as-is when it's not in the catalog options
                        // (e.g. #2/BTR vs catalog #2) — do not soft-rewrite.
                        return item;
                    }
                    const canonical = canonicalizeGrade(matchedGrade);
                    if (canonical && canonical !== item.grade) {
                        needsUpdate = true;
                        const newPrice = getPiecePrice(
                            item.species,
                            canonical,
                            item.dimensions,
                            item.moistureContent,
                        );
                        return {
                            ...item,
                            grade: canonical,
                            unitCost: newPrice > 0 ? newPrice : item.unitCost,
                            total: (newPrice > 0 ? newPrice : item.unitCost) * item.quantity,
                        };
                    }
                    return item;
                });

            const correctedDefaultList = correctGrades(prev.materials.timberList);
            const correctedAlternateSpecies = (prev.materials.alternateSpecies || []).map(species => {
                const correctedList = correctGrades(species.timberList);
                return {
                    ...species,
                    timberList: correctedList,
                    timberTotal: correctedList.reduce((sum, item) => sum + item.total, 0),
                };
            });

            if (!needsUpdate) return prev;

            const materials = {
                ...prev.materials,
                timberList: correctedDefaultList,
                alternateSpecies: correctedAlternateSpecies,
            };
            materials.timberTotal = materials.timberList.reduce((sum, item) => sum + item.total, 0);
            materials.total = recalculateMaterialsTotal(materials);
            const next = { ...prev, materials };
            localNodeSetRef.current = next;
            onUpdate(next);
            return next;
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps -- only when materials DB loads
    }, [timberDatabase, glulamDatabase, disabled]);

    // Live catalog prices on unsent structures (timber, post bases, decking, matching hardware).
    useEffect(() => {
        if (disabled) return;
        if (timberDatabase.length === 0 && glulamDatabase.length === 0 && hardwareDatabase.length === 0) return;

        setLocalNodeSet(prev => {
            const next = syncNodeSetCatalogPrices(prev, timberDatabase, hardwareDatabase, glulamDatabase);
            if (next === prev) return prev;
            localNodeSetRef.current = next;
            onUpdate(next);
            return next;
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps -- only when materials DB loads
    }, [timberDatabase, glulamDatabase, hardwareDatabase, disabled]);

    /** Focus after paint — ref callback focus() was blurring inputs (onBlur → handleBlur → onUpdate) during commit and triggering "Cannot update parent while rendering child". */
    useEffect(() => {
        if (!lightboxOpen) return;
        const t = window.setTimeout(() => lightboxOverlayRef.current?.focus(), 0);
        return () => clearTimeout(t);
    }, [lightboxOpen]);

    useEffect(() => {
        return () => {
            if (productionBoardFeetFlashTimerRef.current) {
                window.clearTimeout(productionBoardFeetFlashTimerRef.current);
            }
        };
    }, []);

    // Calculate totals - adjust for selected species if multi-species is enabled
    const getSelectedSpeciesTimberTotal = () => {
        if (activeSpeciesTab === 'default' || !localNodeSet.materials.quoteMultipleSpecies) {
            return localNodeSet.materials.timberTotal;
        }
        const altSpecies = (localNodeSet.materials.alternateSpecies || []).find(
            s => s.speciesName === activeSpeciesTab
        );
        return altSpecies ? altSpecies.timberTotal : localNodeSet.materials.timberTotal;
    };

    const selectedTimberTotal = getSelectedSpeciesTimberTotal();
    const { rawDelta: speciesKitRawDelta } = speciesTimberKitRawDelta(
        localNodeSet,
        activeSpeciesTab,
    );

    const productionLiveTotal =
        resolveProductionBoardFeet(
            localNodeSet.production,
            getMaterialsTotalBoardFeet(localNodeSet.materials),
        ) * localRates.productionRatePerBdft;
    const structureTotal =
        localNodeSet.design.total +
        productionLiveTotal +
        localNodeSet.materials.total +
        speciesKitRawDelta +
        localNodeSet.freight.total +
        localNodeSet.overhead.total +
        localNodeSet.install.total +
        localNodeSet.travel.total;

    /** Raw install + travel (for footer when “exclude from client” is on). */
    const installTravelRawTotal = localNodeSet.install.total + localNodeSet.travel.total;
    /** Matches client-facing raw when install/travel excluded from quote. */
    const structureTotalPriced = localNodeSet.excludeInstall
        ? structureTotal - installTravelRawTotal
        : structureTotal;
    /** Only show two footer numbers when excluded amounts are non-zero (otherwise they duplicate). */
    const showInternalTotalFooter = !!localNodeSet.excludeInstall && installTravelRawTotal > 0;
    const displayedStructureTotal = showInternalTotalFooter ? structureTotalPriced : structureTotal;

    const installInputsLocked = disabled || !!localNodeSet.excludeInstall;
    const travelInputsLocked = disabled || !!localNodeSet.excludeInstall;

    const pctBadge = (sectionCost: number) => {
        if (structureTotal <= 0) return null;
        const pct = (sectionCost / structureTotal) * 100;
        return <span className="detail-section-pct">{pct < 1 && pct > 0 ? '<1' : Math.round(pct)}%</span>;
    };

    const renderSectionMetrics = (
        sectionCost: number,
        nodeType: ProposalOpsReviewNodeType,
        nodeLabel: string,
    ) => {
        const nodeComments = filterStructureCommentsByNodeType(structureOpsComments, nodeType);
        const showCommentControl = Boolean(onAddOpsReviewComment) && opsReviewActive;

        return (
            <div className="detail-section-metrics">
                {pctBadge(sectionCost)}
                {showCommentControl && (
                    <OpsReviewStructureCommentsPopout
                        comments={nodeComments}
                        canComment={canOpsReviewComment && opsReviewPending}
                        onAddComment={msg => onAddOpsReviewComment!(msg, nodeType, nodeLabel)}
                        sectionLabel={nodeLabel}
                        defaultDealId={dealId || ''}
                        defaultStructures={enabledProposalStructures(allNodeSets)}
                        projectOptions={structureMentionProjectOptions}
                        enableProjectPicker={structureMentionProjectOptions.length > 0}
                    />
                )}
            </div>
        );
    };

    // Recalculate section totals
    const recalculateDesignTotal = (design: typeof localNodeSet.design, ratesOverride = localRates) => {
        return stampStructureDesignTotal(design, ratesOverride).total;
    };

    const recalculateProductionTotal = (production: typeof localNodeSet.production, ratesOverride = localRates) => {
        const materialsBf = recalculateMaterialsBoardFeet(localNodeSet.materials);
        return resolveProductionBoardFeet(production, materialsBf) * ratesOverride.productionRatePerBdft;
    };

    /** Mutates `production` to stamp the per-structure $/bdft alongside the
     *  recomputed total. The rate is the source of truth on reopen, so it
     *  must always be set whenever `total` is recomputed. */
    const stampProductionRateAndTotal = (
        production: typeof localNodeSet.production,
        ratesOverride = localRates,
        materials = localNodeSet.materials,
    ): void => {
        const materialsBf = recalculateMaterialsBoardFeet(materials);
        const boardFeet = resolveProductionBoardFeet(production, materialsBf);
        if (!isProductionBoardFeetOverridden(production, materialsBf)) {
            production.boardFeet = boardFeet;
        }
        production.ratePerBdft = ratesOverride.productionRatePerBdft;
        production.total = boardFeet * ratesOverride.productionRatePerBdft;
    };

    const recalculateMaterialsTotal = (materials: typeof localNodeSet.materials) => {
        const synced = syncHardwareRollups(materials);
        return synced.total;
    };

    const recalculateMaterialsBoardFeet = (materials: typeof localNodeSet.materials) => {
        return getMaterialsTotalBoardFeet(materials);
    };

    const applyProductionBoardFeetFromMaterials = (
        production: typeof localNodeSet.production,
        materialsBoardFeet: number,
    ) => syncProductionBoardFeetFromMaterials(production, materialsBoardFeet);

    // Parent can add timber while this modal stays open (Add Timber / compare-pick).
    // Only adopt when the *parent* list gained ids — do not treat a local delete
    // as a stale parent snapshot and put the lines back.
    useEffect(() => {
        const parentList = nodeSet.materials?.timberList ?? [];
        const parentSig = parentList.map(item => item.id).join('\0');
        if (parentSig === lastParentTimberIdsRef.current) return;
        const prevParentIds = new Set(
            lastParentTimberIdsRef.current.split('\0').filter(Boolean),
        );
        lastParentTimberIdsRef.current = parentSig;
        const gained = parentList.filter(item => !prevParentIds.has(item.id));
        if (gained.length === 0) return;

        const prev = localNodeSetRef.current;
        const localIds = new Set(prev.materials.timberList.map(item => item.id));
        const toAdd = gained.filter(item => !localIds.has(item.id));
        const parentKeptOld = parentList.some(item => prevParentIds.has(item.id));
        // Full parent replace (new id set) vs Add Timber (append new ids).
        const timberList =
            !parentKeptOld && prevParentIds.size > 0
                ? parentList
                : toAdd.length === 0
                    ? prev.materials.timberList
                    : [...prev.materials.timberList, ...toAdd];
        if (timberList === prev.materials.timberList) return;

        const materials = {
            ...prev.materials,
            timberList,
            timberTotal: timberList.reduce((sum, item) => sum + item.total, 0),
        };
        materials.total = recalculateMaterialsTotal(materials);
        materials.totalBoardFeet = recalculateMaterialsBoardFeet(materials);
        const production = applyProductionBoardFeetFromMaterials(prev.production, materials.totalBoardFeet);
        stampProductionRateAndTotal(production, localRates, materials);
        const next = { ...prev, materials, production };
        localNodeSetRef.current = next;
        setLocalNodeSet(next);
        onUpdate(next);
        // eslint-disable-next-line react-hooks/exhaustive-deps -- only when parent timber ids change
    }, [nodeSet.materials?.timberList]);

    const recalculateFreightTotal = (freight: typeof localNodeSet.freight) => {
        const toProductionCost = freightFlatFeeMode.toProduction
            ? freight.flatFeeToAspers
            : freight.milesToAspers * freight.costPerMileToAspers;
        const toJobSiteCost = freightFlatFeeMode.toJobSite
            ? freight.flatFeeToJobSite
            : freight.milesToJobSite * freight.costPerMileToJobSite;
        return toProductionCost + toJobSiteCost;
    };

    const recalculateOverheadTotal = (overhead: typeof localNodeSet.overhead) => {
        return overhead.days * rates.overheadDailyRate;
    };

    const recalculateInstallTotal = (install: typeof localNodeSet.install) => {
        return computeInstallNodeRawTotal(install, rates, {
            excludeInstallDayOverheadRate: structureOverheadLockedByProjectOverhead,
        });
    };

    const recalculateTravelTotal = (
        travel: typeof localNodeSet.travel,
        install: typeof localNodeSet.install = localNodeSet.install,
    ) => computeTravelNodeRawTotal(travel, rates, getInstallLaborLines(install));

    // Handle rate changes (flush so blur/close see updated production totals immediately)
    const handleRateChange = (rateName: keyof typeof localRates, value: number) => {
        const newRates = { ...localRates, [rateName]: value };

        // Recalculate affected sections
        const updated = { ...localNodeSet };
        if (['pmRate', 'apmRate', 'leadDesignerRate', 'supportDesignerRate', 'engineeringRate'].includes(rateName)) {
            updated.design.total = recalculateDesignTotal(updated.design, newRates);
        }
        if (rateName === 'productionRatePerBdft') {
            stampProductionRateAndTotal(updated.production, newRates);
        }
        localNodeSetRef.current = updated;
        flushSync(() => {
            setLocalRates(newRates);
            setLocalNodeSet(updated);
        });
        onUpdate(updated);
    };

    // Handle field changes
    const handleDesignPatch = (patch: Partial<DesignNodeData>) => {
        const design = stampStructureDesignTotal(
            { ...localNodeSet.design, ...patch },
            localRates,
        );
        const updated = { ...localNodeSet, design };
        localNodeSetRef.current = updated;
        setLocalNodeSet(updated);
        if (allowPostSendDesignEdits) onUpdate(updated);
    };


    const handleProductionChange = (field: keyof typeof localNodeSet.production, value: number) => {
        const materialsBf = recalculateMaterialsBoardFeet(localNodeSet.materials);
        const production =
            field === 'boardFeet'
                ? setProductionBoardFeetOverride(localNodeSet.production, value, materialsBf)
                : { ...localNodeSet.production, [field]: value };
        stampProductionRateAndTotal(production);
        const updated = { ...localNodeSet, production };
        localNodeSetRef.current = updated;
        flushSync(() => setLocalNodeSet(updated));
        onUpdate(updated);
    };

    const handleResetProductionBoardFeet = () => {
        const materialsBf = recalculateMaterialsBoardFeet(localNodeSet.materials);
        const production = clearProductionBoardFeetOverride(localNodeSet.production, materialsBf);
        stampProductionRateAndTotal(production);
        const updated = { ...localNodeSet, production };
        localNodeSetRef.current = updated;
        flushSync(() => setLocalNodeSet(updated));
        onUpdate(updated);
    };

    const handleEnableProductionBoardFeetOverride = () => {
        const materialsBf = recalculateMaterialsBoardFeet(localNodeSet.materials);
        const production = enableProductionBoardFeetOverride(localNodeSet.production, materialsBf);
        stampProductionRateAndTotal(production);
        const updated = { ...localNodeSet, production };
        localNodeSetRef.current = updated;
        flushSync(() => setLocalNodeSet(updated));
        onUpdate(updated);
        requestAnimationFrame(() => {
            productionBoardFeetInputRef.current?.focus();
            productionBoardFeetInputRef.current?.select();
        });
    };

    const handleProductionBoardFeetOverrideToggle = (enabled: boolean) => {
        if (enabled) {
            handleEnableProductionBoardFeetOverride();
        } else {
            handleResetProductionBoardFeet();
        }
    };

    const handleHardwareListChange = (hardwareList: HardwareListItem[]) => {
        const materials = syncHardwareRollups({ ...localNodeSet.materials, hardwareList });
        materials.totalBoardFeet = recalculateMaterialsBoardFeet(materials);
        const production = applyProductionBoardFeetFromMaterials(localNodeSet.production, materials.totalBoardFeet);
        stampProductionRateAndTotal(production, localRates, materials);
        setLocalNodeSet({ ...localNodeSet, materials, production });
    };

    const handleMaterialsChange = (field: string, value: number) => {
        let materials = { ...localNodeSet.materials, [field]: value };
        if (field === 'postBasesQty') {
            materials.postBasesTotal = value * materials.postBasesUnitCost;
        }
        if (field === 'postBasesUnitCost') {
            materials.postBasesTotal = materials.postBasesQty * value;
        }
        if (field === 'deckingSqFt' || field === 'deckingWidthInches' || field === 'deckingPricePerLinFt') {
            const sqFt = materials.deckingSqFt || 0;
            const width = materials.deckingWidthInches || 0;
            const price = materials.deckingPricePerLinFt || 0;
            materials.deckingTotal = (width > 0 && sqFt > 0) ? Math.round((sqFt * 12) / width * price) : 0;
        }
        if (field === 'deckingProductId') {
            const product = deckingProducts.find(p => p.id === String(value));
            if (product) {
                materials.deckingWidthInches = product.widthInches || 0;
                materials.deckingPricePerLinFt = product.unitPrice;
                materials.deckingProductName = product.name;
            } else {
                materials.deckingWidthInches = 0;
                materials.deckingPricePerLinFt = 0;
                materials.deckingProductName = undefined;
            }
            const sqFt = materials.deckingSqFt || 0;
            const width = materials.deckingWidthInches || 0;
            const price = materials.deckingPricePerLinFt || 0;
            materials.deckingTotal = (width > 0 && sqFt > 0) ? Math.round((sqFt * 12) / width * price) : 0;
        }
        materials = syncHardwareRollups(materials);
        materials.totalBoardFeet = recalculateMaterialsBoardFeet(materials);

        // Update production board feet
        let production = applyProductionBoardFeetFromMaterials(localNodeSet.production, materials.totalBoardFeet);
        stampProductionRateAndTotal(production, localRates, materials);

        setLocalNodeSet({ ...localNodeSet, materials, production });
    };

    const postBasesRows: PostBasesItem[] =
        localNodeSet.materials.postBasesItems && localNodeSet.materials.postBasesItems.length > 0
            ? localNodeSet.materials.postBasesItems
            : getStructurePostBasesItems(localNodeSet.materials);

    const applyPostBasesItems = (rawItems: PostBasesItem[], commit: boolean) => {
        const materials = syncHardwareRollups(withPostBasesRollups(localNodeSet.materials, rawItems));
        materials.totalBoardFeet = recalculateMaterialsBoardFeet(materials);
        const production = applyProductionBoardFeetFromMaterials(localNodeSet.production, materials.totalBoardFeet);
        stampProductionRateAndTotal(production, localRates, materials);
        const next = { ...localNodeSet, materials, production };
        if (commit) {
            commitLocalNodeSet(next);
        } else {
            setLocalNodeSet(next);
        }
    };

    const newPostBasesItem = (): PostBasesItem => ({
        id: `postbase-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        productName: undefined,
        quantity: 1,
        unitCost: 0,
        total: 0,
    });

    const upsertPostBasesRows = (
        id: string,
        patch: (row: PostBasesItem) => PostBasesItem,
    ): PostBasesItem[] => {
        if (postBasesRows.some((it) => it.id === id)) {
            return postBasesRows.map((it) => (it.id === id ? patch(it) : it));
        }
        return [...postBasesRows, patch({ ...newPostBasesItem(), id })];
    };

    const handleRemovePostBasesItem = (id: string) => {
        applyPostBasesItems(postBasesRows.filter((it) => it.id !== id), true);
    };

    const handlePostBasesItemProduct = (id: string, productId: string) => {
        if (productId === '__current__') return;
        const product = postBaseProducts.find(p => p.id === productId);
        applyPostBasesItems(upsertPostBasesRows(id, (it) => {
            if (!product) return { ...it, productName: undefined, unitCost: 0 };
            return { ...it, productName: product.name, unitCost: product.unitPrice };
        }), true);
    };

    const handlePostBasesItemQty = (id: string, quantity: number) => {
        applyPostBasesItems(upsertPostBasesRows(id, (it) => ({ ...it, quantity })), false);
    };

    const handlePostBasesItemUnitCost = (id: string, unitCost: number) => {
        applyPostBasesItems(upsertPostBasesRows(id, (it) => ({ ...it, unitCost })), false);
    };

    // ---- Decking (multiple sizes per structure) ----
    // Board width is not edited here — it comes from the material database
    // (nominal size in the product name, falling back to the product's width).
    const deckingProductWidth = (productName: string | undefined, fallback = 0): number => {
        const product = deckingProducts.find(p => p.name === productName);
        return resolveDeckingWidthInches(productName, product?.widthInches ?? fallback);
    };

    // Editor rows: the explicit item array once present, otherwise the single
    // item synthesized from legacy fields so old proposals migrate on first edit.
    const deckingRows: DeckingItem[] =
        localNodeSet.materials.deckingItems && localNodeSet.materials.deckingItems.length > 0
            ? localNodeSet.materials.deckingItems
            : getStructureDeckingItems(localNodeSet.materials);

    const applyDeckingItems = (rawItems: DeckingItem[], commit: boolean) => {
        const items = rawItems.map(it => {
            const widthInches = deckingProductWidth(it.productName, it.widthInches);
            const withWidth = { ...it, widthInches };
            return { ...withWidth, total: deckingItemTotal(withWidth) };
        });
        const first = items[0];
        const materials = syncHardwareRollups({
            ...localNodeSet.materials,
            deckingItems: items,
            deckingTotal: items.reduce((sum, it) => sum + it.total, 0),
            deckingSqFt: items.reduce((sum, it) => sum + (it.sqFt || 0), 0),
            deckingProductName: first?.productName,
            deckingWidthInches: first?.widthInches || 0,
            deckingPricePerLinFt: first?.pricePerLinFt || 0,
        });
        materials.totalBoardFeet = recalculateMaterialsBoardFeet(materials);
        const production = applyProductionBoardFeetFromMaterials(localNodeSet.production, materials.totalBoardFeet);
        stampProductionRateAndTotal(production, localRates, materials);
        const next = { ...localNodeSet, materials, production };
        if (commit) {
            commitLocalNodeSet(next);
        } else {
            setLocalNodeSet(next);
        }
    };

    const newDeckingItem = (): DeckingItem => ({
        id: `decking-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        productName: undefined,
        widthInches: 0,
        sqFt: 0,
        pricePerLinFt: 0,
        total: 0,
    });

    const upsertDeckingRows = (
        id: string,
        patch: (row: DeckingItem) => DeckingItem,
    ): DeckingItem[] => {
        if (deckingRows.some((it) => it.id === id)) {
            return deckingRows.map((it) => (it.id === id ? patch(it) : it));
        }
        return [...deckingRows, patch({ ...newDeckingItem(), id })];
    };

    const handleRemoveDeckingItem = (id: string) => {
        applyDeckingItems(deckingRows.filter((it) => it.id !== id), true);
    };

    const handleDeckingItemProduct = (id: string, productId: string) => {
        const product = deckingProducts.find(p => p.id === productId);
        applyDeckingItems(upsertDeckingRows(id, (it) => {
            if (!product) return { ...it, productName: undefined, widthInches: 0, pricePerLinFt: 0 };
            return { ...it, productName: product.name, pricePerLinFt: product.unitPrice };
        }), true);
    };

    const handleDeckingItemSqFt = (id: string, value: number) => {
        applyDeckingItems(upsertDeckingRows(id, (it) => ({ ...it, sqFt: value })), false);
    };

    // Timber list handlers — draft id is rotated after the first keystroke so
    // React keeps focus on the new row instead of creating one line per character.
    const [timberDraftId, setTimberDraftId] = useState(
        () => `timber-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    );

    const emptyTimberDraft = (id: string): TimberListItem => ({
        id,
        name: '',
        species: '',
        grade: '',
        dimensions: '',
        moistureContent: '',
        quantity: 0,
        boardFeet: 0,
        unitCost: 0,
        total: 0,
    });

    const applyTimberUpdates = (item: TimberListItem, updates: Partial<TimberListItem>): TimberListItem => {
        const updated = { ...item, ...updates };
        if (updates.species && !updates.grade) {
            const availableGrades = getGradesForSpecies(updates.species);
            updated.grade = availableGrades[0] || '';
        }
        if ('dimensions' in updates) {
            updated.boardFeet = parseDimensions(updated.dimensions);
        }
        if (updates.species || updates.grade || updates.dimensions || 'moistureContent' in updates) {
            updated.unitCost = getPiecePrice(updated.species, updated.grade, updated.dimensions, updated.moistureContent);
        }
        updated.total = updated.unitCost * updated.quantity;
        return updated;
    };

    const handleUpdateTimber = (id: string, updates: Partial<TimberListItem>) => {
        const materials = {
            ...localNodeSet.materials,
            timberList: id === timberDraftId
                ? [...localNodeSet.materials.timberList, applyTimberUpdates(emptyTimberDraft(timberDraftId), updates)]
                : localNodeSet.materials.timberList.map(item => (
                    item.id === id ? applyTimberUpdates(item, updates) : item
                )),
        };
        materials.timberTotal = materials.timberList.reduce((sum, item) => sum + item.total, 0);
        materials.total = recalculateMaterialsTotal(materials);
        materials.totalBoardFeet = recalculateMaterialsBoardFeet(materials);

        let production = applyProductionBoardFeetFromMaterials(localNodeSet.production, materials.totalBoardFeet);
        stampProductionRateAndTotal(production, localRates, materials);

        const next = { ...localNodeSet, materials, production };
        commitLocalNodeSet(next);
        if (id === timberDraftId) {
            setTimberDraftId(`timber-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`);
        }
    };

    const handleDeleteTimber = (id: string) => {
        if (activeSpeciesTab !== 'default' && localNodeSet.materials.quoteMultipleSpecies) {
            const materials = {
                ...localNodeSet.materials,
                alternateSpecies: (localNodeSet.materials.alternateSpecies || []).map(species => {
                    if (species.speciesName !== activeSpeciesTab) return species;
                    const timberList = species.timberList.filter(item => item.id !== id);
                    return {
                        ...species,
                        timberList,
                        timberTotal: timberList.reduce((sum, item) => sum + item.total, 0),
                        totalBoardFeet: timberList.reduce(
                            (sum, item) => sum + item.boardFeet * item.quantity,
                            0,
                        ),
                    };
                }),
            };
            commitLocalNodeSet({ ...localNodeSet, materials });
            return;
        }

        const materials = {
            ...localNodeSet.materials,
            timberList: localNodeSet.materials.timberList.filter(item => item.id !== id),
        };
        materials.timberTotal = materials.timberList.reduce((sum, item) => sum + item.total, 0);
        materials.total = recalculateMaterialsTotal(materials);
        materials.totalBoardFeet = recalculateMaterialsBoardFeet(materials);

        let production = applyProductionBoardFeetFromMaterials(localNodeSet.production, materials.totalBoardFeet);
        stampProductionRateAndTotal(production, localRates, materials);

        const next = { ...localNodeSet, materials, production };
        commitLocalNodeSet(next);
    };

    const applyBulkAttrsToTimberList = (
        list: typeof localNodeSet.materials.timberList,
    ): typeof localNodeSet.materials.timberList =>
        list.map(item => {
            const updated = {
                ...item,
                species: bulkTimberSpecies.trim(),
                grade: bulkTimberGrade,
                moistureContent: bulkTimberMoisture,
            };
            updated.unitCost = getPiecePrice(
                updated.species,
                updated.grade,
                updated.dimensions,
                updated.moistureContent,
            );
            updated.total = updated.unitCost * updated.quantity;
            return updated;
        });

    const openBulkTimberAttrsModal = () => {
        const list = getCurrentTimberList();
        const first = list[0];
        const fromLast = recommendedTimberDefaults;
        const tabSpecies =
            activeSpeciesTab !== 'default' && speciesOptions.includes(activeSpeciesTab)
                ? activeSpeciesTab
                : null;
        const sp =
            tabSpecies ||
            (fromLast?.species && speciesOptions.includes(fromLast.species)
                ? fromLast.species
                : first?.species && speciesOptions.includes(first.species)
                    ? first.species
                    : speciesOptions[0] || '');
        const grades = getGradesForSpecies(sp);
        const gr =
            fromLast?.grade && grades.includes(fromLast.grade)
                ? fromLast.grade
                : first?.grade && grades.includes(first.grade)
                    ? first.grade
                    : grades[0] || '';
        const mo =
            fromLast?.moistureContent === 'green' || fromLast?.moistureContent === 'kiln_dried'
                ? fromLast.moistureContent
                : first?.moistureContent === 'green' || first?.moistureContent === 'kiln_dried'
                    ? first.moistureContent
                    : '';
        setBulkTimberSpecies(sp);
        setBulkTimberGrade(gr);
        setBulkTimberMoisture(mo);
        setShowBulkTimberAttrsModal(true);
    };

    /** Apply species, grade, and dryness to every row in the active species timber list; recalc $/ea from DB. */
    const handleApplyBulkTimberAttrs = () => {
        if (!bulkTimberSpecies.trim()) return;

        if (activeSpeciesTab === 'default' || !localNodeSet.materials.quoteMultipleSpecies) {
            const materials = {
                ...localNodeSet.materials,
                timberList: applyBulkAttrsToTimberList(localNodeSet.materials.timberList),
            };
            materials.timberTotal = materials.timberList.reduce((sum, item) => sum + item.total, 0);
            materials.total = recalculateMaterialsTotal(materials);
            materials.totalBoardFeet = recalculateMaterialsBoardFeet(materials);

            let production = applyProductionBoardFeetFromMaterials(localNodeSet.production, materials.totalBoardFeet);
            stampProductionRateAndTotal(production, localRates, materials);

            const next = { ...localNodeSet, materials, production };
            setLocalNodeSet(next);
            onUpdate(next);
        } else {
            const materials = {
                ...localNodeSet.materials,
                alternateSpecies: (localNodeSet.materials.alternateSpecies || []).map(species => {
                    if (species.speciesName !== activeSpeciesTab) return species;
                    const timberList = applyBulkAttrsToTimberList(species.timberList);
                    return {
                        ...species,
                        timberList,
                        timberTotal: timberList.reduce((sum, item) => sum + item.total, 0),
                        totalBoardFeet: timberList.reduce(
                            (sum, item) => sum + item.boardFeet * item.quantity,
                            0,
                        ),
                    };
                }),
            };
            const next = { ...localNodeSet, materials };
            setLocalNodeSet(next);
            onUpdate(next);
        }

        onRememberTimberDefaults?.({
            species: bulkTimberSpecies.trim(),
            grade: bulkTimberGrade,
            moistureContent: bulkTimberMoisture,
        });
        setShowBulkTimberAttrsModal(false);
    };

    const applyTimberMaterialsPatch = (patch: Partial<typeof localNodeSet.materials>) => {
        const materials = { ...localNodeSet.materials, ...patch };
        materials.timberTotal = (materials.timberList || []).reduce((sum, item) => sum + item.total, 0);
        if (materials.alternateSpecies) {
            materials.alternateSpecies = materials.alternateSpecies.map(species => ({
                ...species,
                timberTotal: species.timberList.reduce((sum, item) => sum + item.total, 0),
                totalBoardFeet: species.timberList.reduce(
                    (sum, item) => sum + item.boardFeet * item.quantity,
                    0,
                ),
            }));
        }
        materials.total = recalculateMaterialsTotal(materials);
        materials.totalBoardFeet = recalculateMaterialsBoardFeet(materials);

        let production = applyProductionBoardFeetFromMaterials(localNodeSet.production, materials.totalBoardFeet);
        stampProductionRateAndTotal(production, localRates, materials);

        const next = { ...localNodeSet, materials, production };
        setLocalNodeSet(next);
        onUpdate(next);
    };

    useEffect(() => {
        if (autoApplyTimberDefaultsAttemptedRef.current) return;
        if (disabled || !shouldAutoApplyTimberDefaults || !recommendedTimberDefaults) return;
        if (timberDatabase.length === 0) return;
        const list = localNodeSet.materials.timberList;
        if (list.length === 0) return;
        // Persisted timber rows already have species — don't overwrite with session defaults.
        if (list.some(item => item.species?.trim())) {
            autoApplyTimberDefaultsAttemptedRef.current = true;
            onTimberDefaultsAutoApplied?.(nodeSet.id);
            return;
        }

        autoApplyTimberDefaultsAttemptedRef.current = true;

        const timberList = applyTimberDefaultsToTimberList(
            list,
            recommendedTimberDefaults,
            getPiecePrice,
            { preserveExplicitMoisture: true },
        );
        const materials = {
            ...localNodeSet.materials,
            timberList,
        };
        materials.timberTotal = timberList.reduce((sum, item) => sum + item.total, 0);
        materials.total = recalculateMaterialsTotal(materials);
        materials.totalBoardFeet = recalculateMaterialsBoardFeet(materials);

        let production = applyProductionBoardFeetFromMaterials(localNodeSet.production, materials.totalBoardFeet);
        stampProductionRateAndTotal(production, localRates, materials);

        const next = { ...localNodeSet, materials, production };
        setLocalNodeSet(next);
        onUpdate(next);
        onTimberDefaultsAutoApplied?.(nodeSet.id);
        // eslint-disable-next-line react-hooks/exhaustive-deps -- one-shot when structure opens with remembered defaults
    }, [
        disabled,
        shouldAutoApplyTimberDefaults,
        recommendedTimberDefaults,
        timberDatabase.length,
        nodeSet.id,
    ]);

    // Multi-species handlers
    const handleToggleMultiSpecies = (enabled: boolean) => {
        const materials = {
            ...localNodeSet.materials,
            quoteMultipleSpecies: enabled,
            alternateSpecies: enabled ? (localNodeSet.materials.alternateSpecies || []) : [],
        };
        if (!enabled) setAltProductionRateDrafts({});
        setLocalNodeSet({ ...localNodeSet, materials });
    };

    const timberListFromDefault = (speciesName: string) => {
        const timberList = localNodeSet.materials.timberList.map(item => {
            const unitCost = getPiecePrice(speciesName, item.grade, item.dimensions, item.moistureContent);
            return {
                ...item,
                id: `timber-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
                species: speciesName,
                unitCost,
                total: unitCost * item.quantity,
            };
        });
        return {
            timberList,
            timberTotal: timberList.reduce((sum, item) => sum + item.total, 0),
            totalBoardFeet: localNodeSet.materials.totalBoardFeet,
        };
    };

    const handleAddSpecies = (speciesName: string) => {
        const name = speciesName.trim();
        if (!name) return;

        const newSpecies: SpeciesTimberConfig = {
            speciesName: name,
            ...timberListFromDefault(name),
        };

        const materials = {
            ...localNodeSet.materials,
            alternateSpecies: [...(localNodeSet.materials.alternateSpecies || []), newSpecies],
        };
        setLocalNodeSet({ ...localNodeSet, materials });
        setShowAddSpeciesModal(false);
        setNewSpeciesName('');
        setActiveSpeciesTab(name);
    };

    const handleRemoveSpecies = (speciesName: string) => {
        const materials = {
            ...localNodeSet.materials,
            alternateSpecies: (localNodeSet.materials.alternateSpecies || []).filter(
                s => s.speciesName !== speciesName
            ),
        };
        setAltProductionRateDrafts(prev => {
            const next = { ...prev };
            delete next[speciesName];
            return next;
        });
        setLocalNodeSet({ ...localNodeSet, materials });
        setActiveSpeciesTab('default');
    };

    const handleAlternateProductionRate = (speciesName: string, raw: string) => {
        const trimmed = raw.trim();
        const materials = {
            ...localNodeSet.materials,
            alternateSpecies: (localNodeSet.materials.alternateSpecies || []).map(species => {
                if (species.speciesName !== speciesName) return species;
                if (trimmed === '') {
                    const { ratePerBdft: _dropped, ...rest } = species;
                    return rest;
                }
                const parsed = parseFloat(trimmed);
                if (!Number.isFinite(parsed) || parsed < 0) {
                    const { ratePerBdft: _dropped, ...rest } = species;
                    return rest;
                }
                return { ...species, ratePerBdft: parsed };
            }),
        };
        commitLocalNodeSet({ ...localNodeSet, materials });
    };

    const handleCopyFromDefault = (targetSpeciesName: string) => {
        const copied = timberListFromDefault(targetSpeciesName);
        const materials = {
            ...localNodeSet.materials,
            alternateSpecies: (localNodeSet.materials.alternateSpecies || []).map(species => {
                if (species.speciesName !== targetSpeciesName) return species;
                return { ...species, ...copied };
            }),
        };
        setLocalNodeSet({ ...localNodeSet, materials });
    };

    // Update alternate species timber item
    const handleAlternateTimberChange = (
        speciesName: string,
        itemId: string,
        field: keyof TimberListItem,
        value: string | number
    ) => {
        const materials = {
            ...localNodeSet.materials,
            alternateSpecies: (localNodeSet.materials.alternateSpecies || []).map(species => {
                if (species.speciesName !== speciesName) return species;
                const updatedList = species.timberList.map(item => {
                    if (item.id !== itemId) return item;
                    const updated = { ...item, [field]: value };
                    if (field === 'dimensions') {
                        updated.boardFeet = parseDimensions(value as string);
                    }
                    if (field === 'species' || field === 'grade' || field === 'dimensions' || field === 'moistureContent') {
                        const price = getPiecePrice(updated.species, updated.grade, updated.dimensions, updated.moistureContent);
                        updated.unitCost = price;
                        updated.total = price * updated.quantity;
                    }
                    if (field === 'quantity' || field === 'unitCost') {
                        updated.total = updated.unitCost * updated.quantity;
                    }
                    return updated;
                });
                return {
                    ...species,
                    timberList: updatedList,
                    timberTotal: updatedList.reduce((sum, item) => sum + item.total, 0),
                    totalBoardFeet: updatedList.reduce((sum, item) => sum + item.boardFeet * item.quantity, 0),
                };
            }),
        };
        commitLocalNodeSet({ ...localNodeSet, materials });
    };

    // Get current species timber list (default or alternate)
    const getCurrentTimberList = () => {
        if (activeSpeciesTab === 'default') {
            return localNodeSet.materials.timberList;
        }
        const species = (localNodeSet.materials.alternateSpecies || []).find(
            s => s.speciesName === activeSpeciesTab
        );
        return species?.timberList || [];
    };

    const currentTimberList = getCurrentTimberList();
    const timberDraftRow = emptyTimberDraft(timberDraftId);
    const timberDisplayList =
        !disabled && activeSpeciesTab === 'default'
            ? [...currentTimberList, timberDraftRow]
            : currentTimberList;
    useOverflowWheelScroll(
        timberListScrollRef,
        '.detail-modal-body',
        timberDisplayList.length > 0,
    );
    const compareMarks = useMemo(
        () => compareOtherNodeSet
            ? compareStructureLineItems({
                thisNodeSet: localNodeSet,
                otherNodeSet: compareOtherNodeSet,
                thisSpeciesTab: activeSpeciesTab,
            })
            : null,
        [compareOtherNodeSet, localNodeSet, activeSpeciesTab],
    );
    const isComparePane = layout === 'pane';
    const timberListBdftTotal = Math.round(
        currentTimberList.reduce((sum, item) => sum + item.boardFeet * item.quantity, 0)
    );
    const timberListMoneyTotal = currentTimberList.reduce((sum, item) => sum + item.total, 0);
    const productionMaterialsBf = recalculateMaterialsBoardFeet(localNodeSet.materials);
    const productionBfOverridden = isProductionBoardFeetOverridden(
        localNodeSet.production,
        productionMaterialsBf,
    );
    const productionDisplayBf = resolveProductionBoardFeet(
        localNodeSet.production,
        productionMaterialsBf,
    );

    const revealProductionBoardFeet = () => {
        const group = productionBoardFeetGroupRef.current;
        group?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        if (group) {
            group.classList.add('production-board-feet-group--flash');
            if (productionBoardFeetFlashTimerRef.current) {
                window.clearTimeout(productionBoardFeetFlashTimerRef.current);
            }
            productionBoardFeetFlashTimerRef.current = window.setTimeout(() => {
                group.classList.remove('production-board-feet-group--flash');
                productionBoardFeetFlashTimerRef.current = null;
            }, 1400);
        }
        window.setTimeout(() => {
            productionBoardFeetInputRef.current?.focus();
            productionBoardFeetInputRef.current?.select();
        }, 250);
    };

    const handleFreightChange = (field: keyof typeof localNodeSet.freight, value: number) => {
        const freight = { ...localNodeSet.freight, [field]: value };
        freight.total = recalculateFreightTotal(freight);
        setLocalNodeSet({ ...localNodeSet, freight });
    };

    const handleFreightModeToggle = (leg: 'toProduction' | 'toJobSite') => {
        const newMode = { ...freightFlatFeeMode, [leg]: !freightFlatFeeMode[leg] };
        setFreightFlatFeeMode(newMode);

        // Clear the opposite field
        const freight = { ...localNodeSet.freight };
        if (leg === 'toProduction') {
            if (newMode.toProduction) {
                freight.milesToAspers = 0;
            } else {
                freight.flatFeeToAspers = 0;
            }
        } else {
            if (newMode.toJobSite) {
                freight.milesToJobSite = 0;
            } else {
                freight.flatFeeToJobSite = 0;
            }
        }
        freight.total = recalculateFreightTotal(freight);
        setLocalNodeSet({ ...localNodeSet, freight });
    };

    const handleOverheadChange = (field: keyof typeof localNodeSet.overhead, value: number) => {
        const overhead = { ...localNodeSet.overhead, [field]: value };
        overhead.total = recalculateOverheadTotal(overhead);
        setLocalNodeSet({ ...localNodeSet, overhead });
    };

    const handleInstallChange = (field: string, value: number | boolean) => {
        const install = { ...localNodeSet.install, [field]: value };
        if (field === 'craneRequired' && value === true && install.craneRatePerDay == null) {
            install.craneRatePerDay = rates.craneDailyRate;
        }
        if (field === 'telehandlerRequired' && value === true && install.telehandlerRatePerDay == null) {
            install.telehandlerRatePerDay = rates.telehandlerDailyRate;
        }
        if (field === 'scissorLiftRequired' && value === true && install.scissorLiftRatePerDay == null) {
            install.scissorLiftRatePerDay = rates.scissorLiftDailyRate;
        }
        install.total = recalculateInstallTotal(install);
        const travelBase = field === 'days'
            ? withDefaultHotelNights(
                localNodeSet.travel,
                Number(install.days) || 0,
                localNodeSet.install.days,
                localNodeSet.travel.travelDays,
            )
            : localNodeSet.travel;
        const travel = {
            ...travelBase,
            total: recalculateTravelTotal(travelBase, install),
        };
        const updated = { ...localNodeSet, install, travel };
        localNodeSetRef.current = updated;
        setLocalNodeSet(updated);
        onUpdate(updated);
    };

    const handleInstallReplace = (nextInstall: typeof localNodeSet.install) => {
        const install = {
            ...nextInstall,
            total: recalculateInstallTotal(nextInstall),
        };
        const travelBase = install.days !== localNodeSet.install.days
            ? withDefaultHotelNights(
                localNodeSet.travel,
                install.days,
                localNodeSet.install.days,
                localNodeSet.travel.travelDays,
            )
            : localNodeSet.travel;
        const travel = {
            ...travelBase,
            total: recalculateTravelTotal(travelBase, install),
        };
        const updated = { ...localNodeSet, install, travel };
        localNodeSetRef.current = updated;
        setLocalNodeSet(updated);
        onUpdate(updated);
    };

    const handleTravelPatch = (patch: Partial<typeof localNodeSet.travel>) => {
        let travel = { ...localNodeSet.travel, ...patch };
        if (patch.travelDays !== undefined && patch.hotelNights === undefined) {
            travel = withDefaultHotelNights(
                travel,
                localNodeSet.install.days,
                localNodeSet.install.days,
                localNodeSet.travel.travelDays,
            );
        }
        travel.total = recalculateTravelTotal(travel);
        setLocalNodeSet({ ...localNodeSet, travel });
    };

    const handleCopyNodeFrom = (kind: CopyableNodeKind, sourceId: string) => {
        const source = allNodeSets.find((ns) => ns.id === sourceId);
        if (!source || source.id === localNodeSet.id) return;
        let next = copyNodeFromStructure(localNodeSet, source, kind);
        if (kind === 'design') {
            next = { ...next, design: stampStructureDesignTotal(next.design, localRates) };
        } else if (kind === 'install') {
            const travelBase = withDefaultHotelNights(
                next.travel,
                next.install.days,
                localNodeSet.install.days,
                localNodeSet.travel.travelDays,
            );
            next = {
                ...next,
                install: { ...next.install, total: recalculateInstallTotal(next.install) },
                travel: { ...travelBase, total: recalculateTravelTotal(travelBase, next.install) },
            };
        } else if (kind === 'travel') {
            next = {
                ...next,
                travel: { ...next.travel, total: recalculateTravelTotal(next.travel, next.install) },
            };
        } else if (kind === 'freight') {
            setFreightFlatFeeMode({
                toProduction: next.freight.flatFeeToAspers > 0,
                toJobSite: next.freight.flatFeeToJobSite > 0,
            });
        } else if (kind === 'materials' || kind === 'timber') {
            const materials = syncHardwareRollups(next.materials);
            materials.totalBoardFeet = recalculateMaterialsBoardFeet(materials);
            const production = applyProductionBoardFeetFromMaterials(
                localNodeSet.production,
                materials.totalBoardFeet,
            );
            stampProductionRateAndTotal(production, localRates, materials);
            next = { ...next, materials, production };
        }
        commitLocalNodeSet(next);
    };

    // When project OH % is toggled, zero per-structure overhead and recompute install totals.
    useEffect(() => {
        if (disabled) return;
        setLocalNodeSet(prev => {
            const nextInstallTotal = recalculateInstallTotal(prev.install);
            const needsOverheadZero =
                structureOverheadLockedByProjectOverhead &&
                (prev.overhead.days !== 0 || prev.overhead.total !== 0);
            const needsInstallOhZero =
                structureOverheadLockedByProjectOverhead && (prev.install.overheadTotal || 0) !== 0;
            const needsInstallRecalc = Math.abs(nextInstallTotal - prev.install.total) >= 0.01;

            if (!needsOverheadZero && !needsInstallOhZero && !needsInstallRecalc) return prev;

            const next = {
                ...prev,
                ...(needsOverheadZero ? { overhead: { ...prev.overhead, days: 0, total: 0 } } : {}),
                install: {
                    ...prev.install,
                    ...(needsInstallOhZero ? { overheadTotal: 0 } : {}),
                    ...(needsInstallRecalc ? { total: nextInstallTotal } : {}),
                },
            };
            onUpdate(next);
            return next;
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional: only when global PO lock toggles; avoids save loops
    }, [structureOverheadLockedByProjectOverhead, disabled]);

    // Save changes on blur — use ref so the flush from rate changes (same tick as blur) is not overwritten by stale state.
    const handleBlur = () => {
        if (disabled && !allowPostSendDesignEdits) return;
        onUpdate(localNodeSetRef.current);
    };

    const handleClose = useCallback(() => {
        if (!disabled || allowPostSendDesignEdits) {
            onUpdate(localNodeSetRef.current);
            const inferred = inferTimberDefaultsFromList(localNodeSetRef.current.materials.timberList);
            if (inferred) onRememberTimberDefaults?.(inferred);
        }
        onClose();
    }, [disabled, allowPostSendDesignEdits, onUpdate, onClose, onRememberTimberDefaults]);

    // Handle name save
    const handleNameSave = () => {
        onRename(nameValue);
        // Also sync into localNodeSet so any subsequent onUpdate calls don't overwrite the rename
        setLocalNodeSet(prev => ({ ...prev, name: nameValue }));
        setEditingName(false);
    };

    // Handle delete
    const handleDelete = () => {
        if (window.confirm(`Are you sure you want to delete "${nodeSet.name}"?`)) {
            onUpdate(localNodeSetRef.current);
            onDelete();
            onClose();
        }
    };

    // Handle image add
    const handleAddImage = (item: MediaItem) => {
        const currentImages = localNodeSet.images || [];
        const newImages = [...currentImages, { url: item.url, name: item.name }];
        const updated = { ...localNodeSet, images: newImages };
        setLocalNodeSet(updated);
        onUpdate(updated);
    };

    // Handle image remove
    const handleRemoveImage = (index: number) => {
        const currentImages = localNodeSet.images || [];
        const newImages = currentImages.filter((_, i) => i !== index);
        const updated = { ...localNodeSet, images: newImages };
        setLocalNodeSet(updated);
        onUpdate(updated);
    };

    const handleStructureGalleryDragEnter = useCallback((e: React.DragEvent) => {
        if (disabled) return;
        // Ignore non-file drags so we do not fight text/link drags
        if (![...e.dataTransfer.types].includes('Files')) return;
        e.preventDefault();
        e.stopPropagation();
        setStructureGalleryDragOver(true);
    }, [disabled]);

    /** Child elements bubble dragleave; only clear when the pointer actually leaves the gallery bounds. */
    const handleStructureGalleryDragLeave = useCallback((e: React.DragEvent) => {
        if (disabled) return;
        e.preventDefault();
        e.stopPropagation();
        const root = structureGalleryRef.current;
        if (!root) {
            setStructureGalleryDragOver(false);
            return;
        }
        const rect = root.getBoundingClientRect();
        const { clientX: x, clientY: y } = e;
        const pad = 2;
        if (x > rect.left + pad && x < rect.right - pad && y > rect.top + pad && y < rect.bottom - pad) {
            return;
        }
        setStructureGalleryDragOver(false);
    }, [disabled]);

    const handleStructureGalleryDragOver = useCallback((e: React.DragEvent) => {
        if (disabled) return;
        if (![...e.dataTransfer.types].includes('Files')) return;
        e.preventDefault();
        e.stopPropagation();
        e.dataTransfer.dropEffect = 'copy';
    }, [disabled]);

    const handleStructureGalleryDrop = useCallback(
        async (e: React.DragEvent) => {
            if (disabled) return;
            e.preventDefault();
            e.stopPropagation();
            setStructureGalleryDragOver(false);
            if (!user?.id) {
                window.alert('You must be signed in to upload images.');
                return;
            }
            const images = Array.from(e.dataTransfer.files).filter(
                (f) => getMediaType(f.type, f.name) === 'image'
            );
            const valid = images.filter((f) => f.size <= STRUCTURE_IMAGE_MAX_BYTES);
            if (valid.length === 0) return;

            setStructureGalleryUploading(true);
            try {
                for (const file of valid) {
                    const mediaItem = await uploadMediaItem(file, {
                        userId: user.id,
                        userName: user.name || user.email || 'Unknown',
                    });
                    setLocalNodeSet((prev) => {
                        const currentImages = prev.images || [];
                        const newImages = [...currentImages, { url: mediaItem.url, name: mediaItem.name }];
                        const updated = { ...prev, images: newImages };
                        onUpdate(updated);
                        return updated;
                    });
                }
            } catch (err) {
                console.error('[StructureDetailModal] Drop upload failed:', err);
                window.alert(err instanceof Error ? err.message : 'Upload failed');
            } finally {
                setStructureGalleryUploading(false);
            }
        },
        [disabled, user, onUpdate]
    );

    const addSnapshotToGallery = useCallback(
        async (file: File, displayName: string) => {
            if (disabled) return;
            if (!user?.id) {
                window.alert('You must be signed in to upload images.');
                return;
            }
            if (file.size > STRUCTURE_IMAGE_MAX_BYTES) {
                window.alert('Image is too large.');
                return;
            }
            setStructureGalleryUploading(true);
            try {
                const mediaItem = await uploadMediaItem(file, {
                    userId: user.id,
                    userName: user.name || user.email || 'Unknown',
                });
                setLocalNodeSet((prev) => {
                    const currentImages = prev.images || [];
                    const newImages = [...currentImages, { url: mediaItem.url, name: displayName || mediaItem.name }];
                    const updated = { ...prev, images: newImages };
                    onUpdate(updated);
                    return updated;
                });
            } catch (err) {
                console.error('[StructureDetailModal] Snapshot upload failed:', err);
                window.alert(err instanceof Error ? err.message : 'Upload failed');
            } finally {
                setStructureGalleryUploading(false);
            }
        },
        [disabled, user, onUpdate],
    );

    const handlePickPdfPages = useCallback(
        async (choices: BoardSnapshotPdfChoice[]) => {
            if (disabled || choices.length === 0) return;
            setSnapshotImporting(true);
            setSnapshotImportProgress({ current: 0, total: choices.length });
            try {
                for (let i = 0; i < choices.length; i += 1) {
                    const choice = choices[i];
                    setSnapshotImportProgress({ current: i + 1, total: choices.length });
                    const safe = choice.label.replace(/[^\w.-]+/g, '_').slice(0, 80);
                    const file = await renderPdfPageToFile(choice.url, choice.pageNumber, `${safe}.jpg`);
                    await addSnapshotToGallery(file, choice.label);
                }
                setSnapshotPickerOpen(false);
            } catch (err) {
                console.error('[StructureDetailModal] PDF snapshot failed:', err);
                window.alert('Could not import one or more PDF pages.');
            } finally {
                setSnapshotImporting(false);
                setSnapshotImportProgress(null);
            }
        },
        [disabled, addSnapshotToGallery],
    );

    const handlePickModel = useCallback((choice: BoardSnapshotModelChoice) => {
        htmlCaptureCountRef.current = 0;
        setSnapshotPickerOpen(false);
        setHtmlCaptureDoc(choice);
    }, []);

    const handleHtmlCapture = useCallback(
        async (file: File) => {
            const i = htmlCaptureCountRef.current;
            htmlCaptureCountRef.current += 1;
            const viewLabel = i === 0 ? 'snapshot' : `snapshot ${i + 1}`;
            const versionBit = htmlCaptureDoc?.versionLabel ? ` · ${htmlCaptureDoc.versionLabel}` : '';
            const label = htmlCaptureDoc ? `${htmlCaptureDoc.title}${versionBit} · ${viewLabel}` : file.name;
            await addSnapshotToGallery(file, label);
        },
        [addSnapshotToGallery, htmlCaptureDoc],
    );

    const structureGalleryBusy = structureGalleryUploading || snapshotImporting;

    // Handle quantity change
    const handleQuantityChange = (value: number) => {
        const quantity = Math.max(1, value);
        const updated = { ...localNodeSet, quantity };
        setLocalNodeSet(updated);
        onUpdate(updated);
    };

    // Handle notes change
    const handleNotesChange = (notes: string) => {
        const updated = { ...localNodeSet, notes };
        setLocalNodeSet(updated);
        onUpdate(updated);
    };

    const handleExcludeFromGlobalPricesChange = (exclude: boolean) => {
        const globalTotal = globalCosts?.totalGlobalCosts ?? 0;
        if (exclude && globalTotal > 0) {
            const testSets = allNodeSets.map(ns =>
                ns.id === localNodeSet.id ? { ...localNodeSet, excludeFromGlobalPrices: true } : ns
            );
            if (countGlobalCostShareEligible(testSets) === 0) {
                window.alert(
                    'When global project costs are greater than zero, at least one structure must receive the global cost split. Leave another structure included first.'
                );
                return;
            }
        }
        const updated = { ...localNodeSet, excludeFromGlobalPrices: exclude };
        setLocalNodeSet(updated);
        onUpdate(updated);
    };

    const handleExcludeInstallChange = (exclude: boolean) => {
        const updated = { ...localNodeSet, excludeInstall: exclude };
        setLocalNodeSet(updated);
        onUpdate(updated);
    };

    // Export timber list to CSV (all structures)
    const handleExportTimberCSV = () => {
        setShowToolsMenu(false);
        const rows: string[] = [];
        rows.push('Structure,Name,Species,Grade,Dry,Dimensions,Quantity,Board Feet,Price Each,Total');

        allNodeSets.filter((ns) => ns.enabled !== false).forEach(ns => {
            const timberList = ns.materials.timberList || [];
            timberList.forEach(item => {
                const total = item.quantity * item.unitCost;
                const dry = abbreviateTimberDrynessDisplay(item.moistureContent);
                rows.push(`"${ns.name}","${item.name}","${item.species}","${item.grade}","${dry}","${item.dimensions}",${item.quantity},${item.boardFeet},${item.unitCost.toFixed(2)},${total.toFixed(2)}`);
            });
        });

        const csv = rows.join('\n');
        const blob = new Blob([csv], { type: 'text/csv' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'timber-list.csv';
        a.click();
        URL.revokeObjectURL(url);
    };

    const handleExportTimberPDF = () => {
        setShowToolsMenu(false);
        const state = { nodeSets: allNodeSets };
        if (!timberSupplierExportHasContent(state)) {
            window.alert('No timber lines to export. Add timber lists to enabled structures first.');
            return;
        }
        const printWindow = openTimberPrintWindow();
        if (!printWindow) {
            window.alert(TIMBER_SUPPLIER_POPUP_BLOCKED_MSG);
            return;
        }
        void printTimberSupplierToWindow(printWindow, state, exportDealTitle).catch(err => {
            console.error('Timber PDF export failed:', err);
            window.alert(err instanceof Error ? err.message : 'Failed to open timber PDF.');
            try {
                printWindow.close();
            } catch {
                /* ignore */
            }
        });
    };

    return (
        <>
        <div
            className={`structure-detail-overlay${isComparePane ? ' structure-detail-overlay--pane' : ''}`}
            onClick={isComparePane ? undefined : handleClose}
        >
            <div
                className={`structure-detail-modal ${disabled ? 'locked' : ''}${isComparePane ? ' structure-detail-modal--pane' : ''}`}
                onClick={(e) => e.stopPropagation()}
            >
                {/* Header */}
                <div className="detail-modal-header">
                    <h2>
                        <Home size={18} className="detail-modal-header-icon" aria-hidden />
                        {editingName ? (
                            <input
                                type="text"
                                value={nameValue}
                                onChange={(e) => {
                                    setNameValue(e.target.value);
                                }}
                                onBlur={handleNameSave}
                                onKeyDown={(e) => e.key === 'Enter' && handleNameSave()}
                                autoFocus
                                style={{
                                    background: 'rgba(255,255,255,0.2)',
                                    border: 'none',
                                    color: 'inherit',
                                    fontSize: 'inherit',
                                    padding: '4px 8px',
                                    borderRadius: '4px',
                                }}
                            />
                        ) : (
                            <span onClick={() => { setEditingName(true); }} style={{ cursor: 'pointer' }}>
                                {nodeSet.name}
                            </span>
                        )}
                        {comparePaneLabel && (
                            <span className="detail-modal-compare-source">{comparePaneLabel}</span>
                        )}
                    </h2>
                    {projectSharePercent != null && projectSharePercent > 0 && (
                        <span className="detail-modal-header-pct" title="Share of structure raw total">
                            {projectSharePercent}%
                        </span>
                    )}
                    <div className="detail-modal-header-end">
                        <ProposalStructurePresenceBadges cursors={structurePresence.fresh} />
                        <button type="button" className="detail-modal-close" onClick={handleClose} aria-label="Close">
                            <X size={18} />
                        </button>
                    </div>
                </div>

                {/* Structure Meta - Images & Quantity (compact strip for small viewports) */}
                <div className="structure-meta-section">
                    <div className="structure-images-row">
                        <div
                            ref={structureGalleryRef}
                            className={`structure-images-gallery${structureGalleryDragOver ? ' structure-images-gallery--drag-over' : ''}${structureGalleryBusy ? ' structure-images-gallery--uploading' : ''}`}
                            onDragEnter={handleStructureGalleryDragEnter}
                            onDragLeave={handleStructureGalleryDragLeave}
                            onDragOver={handleStructureGalleryDragOver}
                            onDrop={handleStructureGalleryDrop}
                        >
                            {structureGalleryBusy && (
                                <span className="structure-images-gallery-uploading-label" aria-live="polite">
                                    {snapshotImportProgress
                                        ? `Importing ${snapshotImportProgress.current}/${snapshotImportProgress.total}…`
                                        : 'Uploading…'}
                                </span>
                            )}
                            {(localNodeSet.images || []).map((img, idx) => (
                                <div key={idx} className="structure-image-thumb">
                                    <img
                                        src={img.url}
                                        alt={img.name}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setLightboxIndex(idx);
                                            setLightboxOpen(true);
                                        }}
                                        style={{ cursor: 'pointer' }}
                                    />
                                    <button className="remove-image-btn" onClick={() => handleRemoveImage(idx)}>
                                        <X size={12} />
                                    </button>
                                </div>
                            ))}
                            <button type="button" className="add-image-btn" onClick={() => setShowMediaPicker(true)} title="Add photo">
                                <ImageIcon size={18} aria-hidden />
                                <span className="add-image-btn-label">Add</span>
                            </button>
                            {!disabled && projectId && (
                                <button
                                    type="button"
                                    className="capture-image-btn"
                                    onClick={() => setSnapshotPickerOpen(true)}
                                    title="Capture from project plans or 3D model"
                                >
                                    <Camera size={18} aria-hidden />
                                    <span className="capture-image-btn-label">Capture</span>
                                </button>
                            )}
                        </div>
                        <div className="structure-quantity-control">
                            <label className="structure-qty-label">Qty</label>
                            <div className="quantity-input-group">
                                <button onClick={() => handleQuantityChange((localNodeSet.quantity || 1) - 1)}>−</button>
                                <input
                                    type="number"
                                    min="1"
                                    value={localNodeSet.quantity || 1}
                                    onChange={(e) => handleQuantityChange(parseInt(e.target.value) || 1)}
                                />
                                <button onClick={() => handleQuantityChange((localNodeSet.quantity || 1) + 1)}>+</button>
                            </div>
                        </div>
                    </div>
                    {/* Notes — single short field; expands when user types */}
                    <div className="structure-notes-section">
                        <label className="structure-notes-label" htmlFor="structure-notes-input">
                            <FileText size={12} aria-hidden /> Notes
                        </label>
                        <textarea
                            id="structure-notes-input"
                            className="structure-notes-input"
                            value={localNodeSet.notes || ''}
                            onChange={(e) => handleNotesChange(e.target.value)}
                            placeholder="Notes (shown on proposal)…"
                            rows={1}
                        />
                    </div>
                </div>

                {!kitMode && (
                    <div
                        className="structure-global-split-section"
                        title="This structure receives no share of Global Project Costs dollar lines; other enabled structures split that amount. Project overhead % (if on) is still split evenly across all enabled structures."
                    >
                        <label className={`structure-global-split-label${disabled ? ' structure-global-split-label--disabled' : ''}`}>
                            <input
                                type="checkbox"
                                className="structure-global-split-checkbox"
                                checked={!!localNodeSet.excludeFromGlobalPrices}
                                disabled={disabled}
                                onChange={e => handleExcludeFromGlobalPricesChange(e.target.checked)}
                            />
                            <span className="structure-global-split-text">
                                <span className="structure-global-split-title">Exclude from global cost split</span>
                                <span className="structure-global-split-hint">
                                    No global $ share here — split among other structures. Project OH % still applies per structure.
                                </span>
                            </span>
                            <Info size={14} className="structure-global-split-info" aria-hidden />
                        </label>
                    </div>
                )}

                {/* Media Picker Modal */}
                <MediaPicker
                    isOpen={showMediaPicker}
                    onClose={() => setShowMediaPicker(false)}
                    onSelect={handleAddImage}
                    allowedTypes={['image']}
                    title="Add Structure Photo"
                />

                {/* Image Lightbox */}
                {lightboxOpen && (localNodeSet.images || []).length > 0 && (
                    <div
                        className="structure-lightbox-overlay"
                        onClick={() => setLightboxOpen(false)}
                        onKeyDown={(e) => {
                            if (e.key === 'Escape') setLightboxOpen(false);
                            if (e.key === 'ArrowLeft') setLightboxIndex(prev => (prev - 1 + (localNodeSet.images || []).length) % (localNodeSet.images || []).length);
                            if (e.key === 'ArrowRight') setLightboxIndex(prev => (prev + 1) % (localNodeSet.images || []).length);
                        }}
                        tabIndex={0}
                        ref={lightboxOverlayRef}
                    >
                        <button className="lightbox-close" onClick={() => setLightboxOpen(false)}>×</button>
                        {(localNodeSet.images || []).length > 1 && (
                            <>
                                <button
                                    className="lightbox-nav lightbox-prev"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setLightboxIndex(prev => (prev - 1 + (localNodeSet.images || []).length) % (localNodeSet.images || []).length);
                                    }}
                                >
                                    ‹
                                </button>
                                <button
                                    className="lightbox-nav lightbox-next"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setLightboxIndex(prev => (prev + 1) % (localNodeSet.images || []).length);
                                    }}
                                >
                                    ›
                                </button>
                            </>
                        )}
                        <img
                            className="lightbox-image"
                            src={(localNodeSet.images || [])[lightboxIndex]?.url}
                            alt={(localNodeSet.images || [])[lightboxIndex]?.name}
                            onClick={(e) => e.stopPropagation()}
                        />
                        {(localNodeSet.images || []).length > 1 && (
                            <div className="lightbox-counter">
                                {lightboxIndex + 1} / {(localNodeSet.images || []).length}
                            </div>
                        )}
                    </div>
                )}

                {/* Body - Sections Grid */}
                <div className="detail-modal-body">
                    <div
                        className="detail-modal-body-surface"
                        onPointerMove={structurePresence.onPointerMove}
                        onPointerLeave={structurePresence.onPointerLeave}
                    >
                    <div className="detail-sections-grid">
                        {/* Design/Timbers/Production/Materials/Freight — hidden in kit mode */}
                        {!kitMode && (
                            <>
                                {/* Design — full-width grouped table */}
                                <div className="detail-section detail-section-ops detail-section-wide">
                                    <div className="detail-section-header">
                                        <div className="detail-section-header-title">
                                            <Pencil size={16} />
                                            <h3>Design</h3>
                                            <span className="detail-section-total">{formatCurrency(localNodeSet.design.total)}</span>
                                        </div>
                                        <div className="detail-section-header-actions">
                                            {renderSectionMetrics(localNodeSet.design.total, 'design', 'Design')}
                                            <CopyNodeFromSelect
                                                structures={allNodeSets}
                                                currentId={localNodeSet.id}
                                                disabled={disabled}
                                                onPick={(id) => handleCopyNodeFrom('design', id)}
                                            />
                                        </div>
                                    </div>
                                    <p className="design-phase-hint">
                                        Estimating / modeling lives under Global Costs → Project overhead.
                                    </p>
                                    <DesignBreakdownGroups
                                        design={localNodeSet.design}
                                        rates={localRates}
                                        hoursDisabled={disabled && !allowPostSendDesignEdits}
                                        ratesDisabled={disabled}
                                        formatCurrency={formatCurrency}
                                        onDesignChange={handleDesignPatch}
                                        onRateChange={(field, value) => handleRateChange(field, value)}
                                        onBlur={handleBlur}
                                        compareFields={compareMarks?.fields}
                                    />
                                </div>
                                {/* Timbers */}
                                <div className="detail-section detail-section-wide">
                                    <div className="detail-section-header">
                                        <div className="detail-section-header-title">
                                            <Package size={16} />
                                            <h3>Timbers</h3>
                                            <span className="detail-section-total">{formatCurrency(selectedTimberTotal)}</span>
                                        </div>
                                        <div className="detail-section-header-actions">
                                            {renderSectionMetrics(selectedTimberTotal, 'timber', 'Timbers')}
                                            <CopyNodeFromSelect
                                                structures={allNodeSets}
                                                currentId={localNodeSet.id}
                                                disabled={disabled}
                                                onPick={(id) => handleCopyNodeFrom('timber', id)}
                                            />
                                            {currentTimberList.length > 0 && (
                                                <button
                                                    type="button"
                                                    className="timber-list-bdft-ref"
                                                    onClick={revealProductionBoardFeet}
                                                    title="Jump to Production board feet"
                                                >
                                                    {timberListBdftTotal.toLocaleString()} bdft
                                                </button>
                                            )}
                                            <span className="timber-color-key">
                                                <span className="key-swatch key-red"></span> Unpriced
                                                <span className="key-swatch key-orange"></span> Negative Qty
                                                <span className="key-swatch key-purple"></span> Non-standard
                                                <span className="key-swatch key-amber"></span> Order length &lt; 8&apos;
                                                {compareOtherNodeSet && (
                                                    <>
                                                        <span className="key-swatch key-compare-changed"></span> Differs
                                                        <span className="key-swatch key-compare-only"></span> Only here
                                                    </>
                                                )}
                                            </span>
                                        </div>
                                    </div>

                                    {/* Quote Additional Species Toggle */}
                                    <div className="multi-species-toggle">
                                        <label className="toggle-label">
                                            <input
                                                type="checkbox"
                                                checked={localNodeSet.materials.quoteMultipleSpecies || false}
                                                onChange={(e) => handleToggleMultiSpecies(e.target.checked)}
                                            />
                                            <span>Quote Additional Species</span>
                                        </label>
                                    </div>

                                    {/* Species Tabs - only show when multi-species is enabled */}
                                    {localNodeSet.materials.quoteMultipleSpecies && (
                                        <div className="species-tabs">
                                            <div className="species-tabs-list">
                                                <button
                                                    className={`species-tab ${activeSpeciesTab === 'default' ? 'active' : ''}`}
                                                    onClick={() => setActiveSpeciesTab('default')}
                                                >
                                                    {localNodeSet.materials.timberList[0]?.species || 'Default'}
                                                    <span className="species-tab-total">
                                                        {formatCurrency(localNodeSet.materials.timberTotal)}
                                                    </span>
                                                </button>
                                                {(localNodeSet.materials.alternateSpecies || []).map(species => (
                                                    <button
                                                        key={species.speciesName}
                                                        className={`species-tab ${activeSpeciesTab === species.speciesName ? 'active' : ''}`}
                                                        onClick={() => setActiveSpeciesTab(species.speciesName)}
                                                    >
                                                        {species.speciesName}
                                                        <span className="species-tab-total">
                                                            {formatCurrency(species.timberTotal)}
                                                        </span>
                                                        <span
                                                            className="species-tab-remove"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                handleRemoveSpecies(species.speciesName);
                                                            }}
                                                        >
                                                            ×
                                                        </span>
                                                    </button>
                                                ))}
                                            </div>
                                            <div className="species-tabs-actions">
                                                <button
                                                    type="button"
                                                    className="species-tabs-action-btn"
                                                    onClick={() => setShowAddSpeciesModal(true)}
                                                >
                                                    <Plus size={14} /> Add Species
                                                </button>
                                                {activeSpeciesTab !== 'default' && (
                                                    <button
                                                        type="button"
                                                        className="species-tabs-action-btn"
                                                        onClick={() => handleCopyFromDefault(activeSpeciesTab)}
                                                        title="Copy timber list from the first species and apply this tab’s pricing"
                                                    >
                                                        <Copy size={14} /> Copy From 1st
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                    )}

                                    {/* Add Species Modal */}
                                    {showAddSpeciesModal && (
                                        <div className="add-species-modal">
                                            <div className="add-species-modal-content">
                                                <h4>Add Alternate Species</h4>
                                                <select
                                                    value={newSpeciesName}
                                                    onChange={(e) => setNewSpeciesName(e.target.value)}
                                                >
                                                    <option value="">Select species...</option>
                                                    {speciesOptions.filter(s =>
                                                        s !== (localNodeSet.materials.timberList[0]?.species || '') &&
                                                        !(localNodeSet.materials.alternateSpecies || []).some(alt => alt.speciesName === s)
                                                    ).map((species: string) => (
                                                        <option key={species} value={species} title={species}>{abbreviateSpeciesName(species)}</option>
                                                    ))}
                                                </select>
                                                <div className="add-species-modal-actions">
                                                    <button onClick={() => { setShowAddSpeciesModal(false); setNewSpeciesName(''); }}>
                                                        Cancel
                                                    </button>
                                                    <button
                                                        className="primary"
                                                        onClick={() => handleAddSpecies(newSpeciesName)}
                                                        disabled={!newSpeciesName}
                                                    >
                                                        Add Species
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    {showBulkTimberAttrsModal && (
                                        <div
                                            className="add-species-modal"
                                            role="dialog"
                                            aria-modal="true"
                                            aria-labelledby="bulk-timber-attrs-title"
                                            onClick={() => setShowBulkTimberAttrsModal(false)}
                                        >
                                            <div
                                                className="add-species-modal-content bulk-timber-attrs-modal-content"
                                                onClick={(e) => e.stopPropagation()}
                                            >
                                                <h4 id="bulk-timber-attrs-title">
                                                    Set all timber rows
                                                    {activeSpeciesTab !== 'default' && (
                                                        <span className="bulk-timber-attrs-tab"> — {activeSpeciesTab}</span>
                                                    )}
                                                </h4>
                                                <p className="bulk-timber-attrs-hint">
                                                    Species, grade, and dryness apply to every line in this species tab. Prices refresh from the materials database (same as editing each row).
                                                    {recommendedTimberDefaults &&
                                                        bulkTimberSpecies === recommendedTimberDefaults.species &&
                                                        bulkTimberGrade === recommendedTimberDefaults.grade &&
                                                        bulkTimberMoisture === recommendedTimberDefaults.moistureContent && (
                                                            <> Pre-filled from your last structure.</>
                                                        )}
                                                </p>
                                                <div className="bulk-timber-attrs-field">
                                                    <label htmlFor="bulk-timber-species">Species</label>
                                                    <select
                                                        id="bulk-timber-species"
                                                        value={bulkTimberSpecies}
                                                        onChange={(e) => {
                                                            const sp = e.target.value;
                                                            setBulkTimberSpecies(sp);
                                                            const grades = getGradesForSpecies(sp);
                                                            setBulkTimberGrade((prev) =>
                                                                grades.includes(prev) ? prev : grades[0] || '',
                                                            );
                                                        }}
                                                    >
                                                        {speciesOptions.map((species: string) => (
                                                            <option key={species} value={species} title={species}>
                                                                {abbreviateSpeciesName(species)}
                                                            </option>
                                                        ))}
                                                    </select>
                                                </div>
                                                <div className="bulk-timber-attrs-field">
                                                    <label htmlFor="bulk-timber-grade">Grade</label>
                                                    <select
                                                        id="bulk-timber-grade"
                                                        value={bulkTimberGrade}
                                                        onChange={(e) => setBulkTimberGrade(e.target.value)}
                                                    >
                                                        {getGradesForSpecies(bulkTimberSpecies).length === 0 ? (
                                                            <option value="">—</option>
                                                        ) : (
                                                            getGradesForSpecies(bulkTimberSpecies).map((grade: string) => (
                                                                <option key={grade} value={grade}>
                                                                    {grade}
                                                                </option>
                                                            ))
                                                        )}
                                                    </select>
                                                </div>
                                                <div className="bulk-timber-attrs-field">
                                                    <label htmlFor="bulk-timber-dry">Dryness</label>
                                                    <select
                                                        id="bulk-timber-dry"
                                                        value={bulkTimberMoisture}
                                                        onChange={(e) =>
                                                            setBulkTimberMoisture(e.target.value as '' | 'green' | 'kiln_dried')
                                                        }
                                                        title="Green (GRN) vs kiln dried (KD); matches materials database"
                                                    >
                                                        <option value="">— (unspecified)</option>
                                                        <option value="green">GRN (green)</option>
                                                        <option value="kiln_dried">KD (kiln dried)</option>
                                                    </select>
                                                </div>
                                                <div className="add-species-modal-actions">
                                                    <button
                                                        type="button"
                                                        onClick={() => setShowBulkTimberAttrsModal(false)}
                                                    >
                                                        Cancel
                                                    </button>
                                                    <button
                                                        type="button"
                                                        className="primary"
                                                        onClick={handleApplyBulkTimberAttrs}
                                                        disabled={!bulkTimberSpecies.trim()}
                                                    >
                                                        Apply to all rows
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    {/* Timber List */}
                                    <div className="timber-toolbar-row">
                                        {!kitMode && dealId && (
                                            <StructureTimberVersionsPanel
                                                dealId={dealId}
                                                nodeSet={localNodeSet}
                                                disabled={disabled}
                                                userId={user?.id}
                                                userName={user?.name || user?.email}
                                                onApplyMaterials={applyTimberMaterialsPatch}
                                            />
                                        )}
                                        <div className="timber-list-actions">
                                            {currentTimberList.length > 0 && !disabled && (
                                                <button
                                                    type="button"
                                                    className="timber-bulk-attrs-btn"
                                                    onClick={openBulkTimberAttrsModal}
                                                    title="Set species, grade, and dryness for every row in this species tab"
                                                >
                                                    <SlidersHorizontal size={14} aria-hidden />
                                                    <span>Set all…</span>
                                                </button>
                                            )}
                                            {activeSpeciesTab === 'default' && onAddTimber && (
                                                <button
                                                    type="button"
                                                    className="timber-btn"
                                                    onClick={onAddTimber}
                                                    disabled={disabled}
                                                    title="Pick a project timber CSV and add selected lines to this structure"
                                                >
                                                    <TreePine size={14} /> Add Timber
                                                </button>
                                            )}
                                            <div className="tools-dropdown-wrapper">
                                                <button className="timber-btn tools-btn" onClick={() => setShowToolsMenu(!showToolsMenu)}>
                                                    <Download size={14} /> Tools <ChevronDown size={12} />
                                                </button>
                                                {showToolsMenu && (
                                                    <div className="tools-dropdown-menu">
                                                        <button onClick={handleExportTimberCSV}>
                                                            <Download size={14} /> Export Timber CSV
                                                        </button>
                                                        <button onClick={handleExportTimberPDF}>
                                                            <Download size={14} /> Export Timber PDF
                                                        </button>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                    <div className="timber-list-container">
                                        <div className="timber-list-header">
                                            <span>
                                                Timber List ({currentTimberList.length} items)
                                                {currentTimberList.some(item => item.unitCost === 0) && (
                                                    <span style={{ color: '#dc2626', marginLeft: '8px', fontWeight: 500 }}>⚠ Pricing Missing</span>
                                                )}
                                            </span>
                                        </div>

                                        {timberDisplayList.length > 0 ? (
                                            <div className="timber-list-scroll" ref={timberListScrollRef}>
                                            <table className={`timber-table${disabled ? ' timber-table--readonly' : ''}`}>
                                                <thead>
                                                    <tr>
                                                        <th className="timber-col-name">Name</th>
                                                        <th className="timber-col-species">Species</th>
                                                        <th className="timber-col-grade">Grade</th>
                                                        <th className="timber-col-dry">Dry</th>
                                                        <th className="timber-col-dims">Dims</th>
                                                        <th className="timber-col-qty">Qty</th>
                                                        <th className="timber-col-bdft">BdFt</th>
                                                        <th className="timber-col-each">$/ea</th>
                                                        <th className="timber-col-total">Total</th>
                                                        <th className="timber-col-sup">Sup</th>
                                                        <th className="timber-col-action"></th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {timberDisplayList.map(item => {
                                                        const isDraft = item.id === timberDraftId;
                                                        const compareEntry = !isDraft ? compareMarks?.timber[item.id] : undefined;
                                                        const compareClass = structureLineCompareRowClass(compareEntry, 'timber');
                                                        return (
                                                        <tr
                                                            key={item.id}
                                                            className={`${!isDraft && item.unitCost === 0 ? 'timber-row-unpriced' : ''} ${item.quantity < 0 ? 'timber-row-warning' : ''} ${!isDraft && item.unitCost > 0 && hasOddDimension(item.dimensions) ? 'timber-row-odd-dim' : ''} ${!isDraft && timberLineHasOrderLengthUnder8Feet(item) ? 'timber-row-short-length' : ''}${isDraft ? ' timber-row-draft' : ''}${compareClass ? ` ${compareClass}` : ''}`}
                                                            title={compareEntry ? formatStructureLineCompareTitle(compareEntry) : undefined}
                                                        >
                                                            <td className="timber-col-name">
                                                                <input
                                                                    type="text"
                                                                    value={item.name}
                                                                    title={item.sourceProductionNumber?.trim()
                                                                        ? `Production #${item.sourceProductionNumber.trim()}`
                                                                        : undefined}
                                                                    onChange={(e) => activeSpeciesTab === 'default'
                                                                        ? handleUpdateTimber(item.id, { name: e.target.value })
                                                                        : handleAlternateTimberChange(activeSpeciesTab, item.id, 'name', e.target.value)
                                                                    }
                                                                    onBlur={handleBlur}
                                                                    placeholder={isDraft ? 'Add item' : 'Piece name'}
                                                                />
                                                            </td>
                                                            <td className="timber-col-species">
                                                                <select
                                                                    value={item.species}
                                                                    title={item.species}
                                                                    onChange={(e) => activeSpeciesTab === 'default'
                                                                        ? handleUpdateTimber(item.id, { species: e.target.value })
                                                                        : handleAlternateTimberChange(activeSpeciesTab, item.id, 'species', e.target.value)
                                                                    }
                                                                    onBlur={handleBlur}
                                                                >
                                                                    {(isDraft || !item.species) && <option value="" />}
                                                                    {speciesOptions.map((species: string) => (
                                                                        <option key={species} value={species} title={species}>{abbreviateSpeciesName(species)}</option>
                                                                    ))}
                                                                </select>
                                                            </td>
                                                            <td className="timber-col-grade">
                                                                <select
                                                                    value={item.grade}
                                                                    onChange={(e) => activeSpeciesTab === 'default'
                                                                        ? handleUpdateTimber(item.id, { grade: e.target.value })
                                                                        : handleAlternateTimberChange(activeSpeciesTab, item.id, 'grade', e.target.value)
                                                                    }
                                                                    onBlur={handleBlur}
                                                                >
                                                                    {(isDraft || !item.grade) && <option value="" />}
                                                                    {getGradesForSpecies(item.species, item.grade).map((grade: string) => (
                                                                        <option key={grade} value={grade}>{grade}</option>
                                                                    ))}
                                                                </select>
                                                            </td>
                                                            <td className="timber-col-dry">
                                                                <select
                                                                    value={item.moistureContent ?? ''}
                                                                    onChange={(e) => {
                                                                        const v = e.target.value as '' | 'green' | 'kiln_dried';
                                                                        return activeSpeciesTab === 'default'
                                                                            ? handleUpdateTimber(item.id, { moistureContent: v })
                                                                            : handleAlternateTimberChange(activeSpeciesTab, item.id, 'moistureContent', v);
                                                                    }}
                                                                    onBlur={handleBlur}
                                                                    title="Green (GRN) vs kiln dried (KD); matches materials database"
                                                                >
                                                                    <option value="">{isDraft ? '' : '—'}</option>
                                                                    <option value="green">GRN</option>
                                                                    <option value="kiln_dried">KD</option>
                                                                </select>
                                                            </td>
                                                            <td className="timber-col-dims">
                                                                <input
                                                                    type="text"
                                                                    value={item.dimensions}
                                                                    onChange={(e) => activeSpeciesTab === 'default'
                                                                        ? handleUpdateTimber(item.id, { dimensions: e.target.value })
                                                                        : handleAlternateTimberChange(activeSpeciesTab, item.id, 'dimensions', e.target.value)
                                                                    }
                                                                    onBlur={handleBlur}
                                                                />
                                                            </td>
                                                            <td className="timber-col-qty">
                                                                <input
                                                                    type="number"
                                                                    value={item.quantity || ''}
                                                                    onChange={(e) => activeSpeciesTab === 'default'
                                                                        ? handleUpdateTimber(item.id, { quantity: parseFloat(e.target.value) || 0 })
                                                                        : handleAlternateTimberChange(activeSpeciesTab, item.id, 'quantity', parseFloat(e.target.value) || 0)
                                                                    }
                                                                    onBlur={handleBlur}
                                                                />
                                                            </td>
                                                            <td className="timber-col-bdft">
                                                                <span>{isDraft ? '' : Math.round(item.boardFeet * item.quantity)}</span>
                                                            </td>
                                                            <td className="timber-col-each">
                                                                <span>{isDraft ? '' : `$${Math.round(item.unitCost)}`}</span>
                                                            </td>
                                                            <td className="timber-col-total timber-total">
                                                                <span>{isDraft ? '' : formatCurrency(item.total)}</span>
                                                            </td>
                                                            <td className="timber-col-sup td-supplier" title={(() => {
                                                                if (!disabled && canQuickAddTimberEstimate(item)) {
                                                                    return 'Add estimate price to materials database';
                                                                }
                                                                const match = findTimberCatalogMatch(
                                                                    timberDatabase,
                                                                    item.species,
                                                                    item.grade,
                                                                    item.dimensions,
                                                                    item.moistureContent,
                                                                );
                                                                return match?.supplier || '';
                                                            })()}>
                                                                {!isDraft && !disabled && canQuickAddTimberEstimate(item) ? (
                                                                    <button
                                                                        type="button"
                                                                        className="timber-quick-add-btn"
                                                                        onClick={() => setQuickAddTimberItem(item)}
                                                                        title="Quick add estimate price"
                                                                        aria-label="Quick add estimate price"
                                                                    >
                                                                        <Plus size={14} />
                                                                    </button>
                                                                ) : isDraft ? (
                                                                    ''
                                                                ) : (
                                                                    <span>{getSupplierInitials(item.species, item.grade, item.dimensions, item.moistureContent)}</span>
                                                                )}
                                                            </td>
                                                            <td className="timber-col-action">
                                                                {!isDraft && !disabled && (
                                                                    <button
                                                                        type="button"
                                                                        className="timber-delete-btn"
                                                                        onClick={() => handleDeleteTimber(item.id)}
                                                                        title="Delete timber line"
                                                                        aria-label="Delete timber line"
                                                                    >
                                                                        <Trash2 size={14} />
                                                                    </button>
                                                                )}
                                                            </td>
                                                        </tr>
                                                        );
                                                    })}
                                                </tbody>
                                                <tfoot>
                                                    <tr>
                                                        <td className="timber-col-name" colSpan={6}><strong>Totals</strong></td>
                                                        <td className="timber-col-bdft timber-bdft-total-cell">
                                                            <button
                                                                type="button"
                                                                className="timber-list-bdft-ref timber-list-bdft-ref--cell"
                                                                onClick={revealProductionBoardFeet}
                                                                title="Jump to Production board feet"
                                                            >
                                                                {timberListBdftTotal.toLocaleString()} bdft
                                                            </button>
                                                        </td>
                                                        <td className="timber-col-each"></td>
                                                        <td className="timber-col-total timber-total"><strong>{formatCurrency(timberListMoneyTotal)}</strong></td>
                                                        <td className="timber-col-sup"></td>
                                                        <td className="timber-col-action"></td>
                                                    </tr>
                                                </tfoot>
                                            </table>
                                            </div>
                                        ) : (
                                            <div className="timber-empty">
                                                No timber items. Import a CSV or type a new row.
                                            </div>
                                        )}
                                    </div>

                                </div>

                                {/* Production — full-width grouped table */}
                                <div className={`detail-section detail-section-ops detail-section-wide${productionBfOverridden ? ' detail-section-override-warn' : ''}`}>
                                    <div className="detail-section-header">
                                        <div className="detail-section-header-title">
                                            <Factory size={16} />
                                            <h3>Production</h3>
                                            <span className="detail-section-total">{formatCurrency(productionLiveTotal)}</span>
                                        </div>
                                        <div className="detail-section-header-actions">
                                            {renderSectionMetrics(productionLiveTotal, 'production', 'Production')}
                                            {productionBfOverridden && (
                                                <span
                                                    className="production-bf-override-badge"
                                                    title="Board feet is a manual override and will not update from the timber list"
                                                >
                                                    <AlertTriangle size={12} aria-hidden />
                                                    Not from Timbers
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                    <ProductionBreakdownGroups
                                        groupRef={productionBoardFeetGroupRef}
                                        boardFeetRef={productionBoardFeetInputRef}
                                        boardFeet={productionDisplayBf}
                                        boardFeetLabel={productionBfOverridden ? 'Joinery (override)' : 'Joinery'}
                                        boardFeetReadOnly={!productionBfOverridden || disabled}
                                        ratePerBdft={localRates.productionRatePerBdft}
                                        formatCurrency={formatCurrency}
                                        disabled={disabled}
                                        overrideEnabled={productionBfOverridden}
                                        overrideDisabled={disabled}
                                        onBoardFeetChange={(value) => handleProductionChange('boardFeet', value)}
                                        onRateChange={(value) => handleRateChange('productionRatePerBdft', value)}
                                        onOverrideToggle={handleProductionBoardFeetOverrideToggle}
                                        onBlur={handleBlur}
                                        alternateSpecies={
                                            localNodeSet.materials.quoteMultipleSpecies
                                                ? localNodeSet.materials.alternateSpecies
                                                : undefined
                                        }
                                        altRateDrafts={altProductionRateDrafts}
                                        onAltRateDraft={(speciesName, value) =>
                                            setAltProductionRateDrafts((prev) => ({ ...prev, [speciesName]: value }))
                                        }
                                        onAltRateCommit={(speciesName, value) => {
                                            handleAlternateProductionRate(speciesName, value);
                                            setAltProductionRateDrafts((prev) => {
                                                const next = { ...prev };
                                                delete next[speciesName];
                                                return next;
                                            });
                                        }}
                                    />
                                </div>

                                {/* Materials — after Production */}
                                <div className="detail-section detail-section-wide">
                                    <div className="detail-section-header">
                                        <div className="detail-section-header-title">
                                            <Hammer size={16} />
                                            <h3>Materials</h3>
                                            <span className="detail-section-total">{formatCurrency(
                                                hardwareListTotal(localNodeSet.materials.hardwareList) +
                                                (localNodeSet.materials.postBasesTotal || 0) +
                                                (localNodeSet.materials.deckingTotal || 0)
                                            )}</span>
                                        </div>
                                        <div className="detail-section-header-actions">
                                            {renderSectionMetrics(
                                                hardwareListTotal(localNodeSet.materials.hardwareList) +
                                                (localNodeSet.materials.postBasesTotal || 0) +
                                                (localNodeSet.materials.deckingTotal || 0),
                                                'materials',
                                                'Materials',
                                            )}
                                            <CopyNodeFromSelect
                                                structures={allNodeSets}
                                                currentId={localNodeSet.id}
                                                disabled={disabled}
                                                onPick={(id) => handleCopyNodeFrom('materials', id)}
                                            />
                                        </div>
                                    </div>
                                    <HardwareListTable
                                        items={localNodeSet.materials.hardwareList || []}
                                        disabled={disabled}
                                        formatCurrency={formatCurrency}
                                        onChange={handleHardwareListChange}
                                        onBlur={handleBlur}
                                        compareMarks={compareMarks ? {
                                            hardware: compareMarks.hardware,
                                            decking: compareMarks.decking,
                                            postBases: compareMarks.postBases,
                                        } : undefined}
                                        postBases={{
                                            rows: postBasesRows,
                                            products: postBaseProducts,
                                            onRemove: handleRemovePostBasesItem,
                                            onProduct: handlePostBasesItemProduct,
                                            onQty: handlePostBasesItemQty,
                                            onUnitCost: handlePostBasesItemUnitCost,
                                        }}
                                        decking={{
                                            rows: deckingRows,
                                            products: deckingProducts,
                                            productWidth: deckingProductWidth,
                                            onRemove: handleRemoveDeckingItem,
                                            onProduct: handleDeckingItemProduct,
                                            onSqFt: handleDeckingItemSqFt,
                                        }}
                                    />
                                </div>

                            </>
                        )}

                        {/* Install Section - With adjustable equipment rates */}
                        {!hideInstallTravel && (
                            <div
                                className={`detail-section detail-section-ops detail-section-wide ${localNodeSet.excludeInstall ? 'detail-section-install-excluded detail-section-install-muted' : ''}`}
                            >
                                <div className="detail-section-header">
                                    <div className="detail-section-header-title">
                                        <Wrench size={16} />
                                        <h3>Installation</h3>
                                        {structureOverheadLockedByProjectOverhead && (
                                            <span
                                                className="install-po-info-badge"
                                                title="Install day cost uses the team labor rate only. The overhead daily rate is not applied to install days while project overhead (% of total) is on in Global Project Costs."
                                            >
                                                <Info size={14} strokeWidth={2.5} aria-hidden />
                                            </span>
                                        )}
                                        <span className="detail-section-total">{formatCurrency(localNodeSet.install.total)}</span>
                                    </div>
                                    <div className="detail-section-header-actions">
                                        {renderSectionMetrics(localNodeSet.install.total, 'install', 'Installation')}
                                        <CopyNodeFromSelect
                                            structures={allNodeSets}
                                            currentId={localNodeSet.id}
                                            disabled={disabled}
                                            onPick={(id) => handleCopyNodeFrom('install', id)}
                                        />
                                    </div>
                                </div>
                                <label className={`exclude-install-toggle ${disabled ? 'exclude-install-toggle--disabled' : ''}`}>
                                    <input
                                        type="checkbox"
                                        checked={!!localNodeSet.excludeInstall}
                                        disabled={disabled}
                                        onChange={e => handleExcludeInstallChange(e.target.checked)}
                                    />
                                    <span>Exclude install &amp; travel from client pricing</span>
                                </label>
                                {localNodeSet.excludeInstall && (
                                    <p className="exclude-install-hint">
                                        Install and travel fields below are greyed out; values are kept for internal reference only and are not billed to the client.
                                    </p>
                                )}
                                <div className={localNodeSet.excludeInstall ? 'install-section-body-muted' : undefined}>
                                <InstallBreakdownGroups
                                    install={localNodeSet.install}
                                    proposalNodeSets={allNodeSets}
                                    rates={rates}
                                    disabled={installInputsLocked}
                                    formatCurrency={formatCurrency}
                                    onInstallChange={handleInstallReplace}
                                    onDaysChange={(days) => handleInstallChange('days', days)}
                                    onBlur={handleBlur}
                                    compareFields={compareMarks?.fields}
                                />
                                </div>
                            </div>
                        )}

                        {/* Travel Section */}
                        {!hideInstallTravel && (
                            <div className={`detail-section detail-section-ops detail-section-wide ${localNodeSet.excludeInstall ? 'detail-section-travel-muted' : ''}`}>
                                <div className="detail-section-header">
                                    <div className="detail-section-header-title">
                                        <Car size={16} />
                                        <h3>Travel</h3>
                                        <span className="detail-section-total">{formatCurrency(localNodeSet.travel.total)}</span>
                                    </div>
                                    <div className="detail-section-header-actions">
                                        {renderSectionMetrics(localNodeSet.travel.total, 'travel', 'Travel')}
                                        <CopyNodeFromSelect
                                            structures={allNodeSets}
                                            currentId={localNodeSet.id}
                                            disabled={disabled}
                                            onPick={(id) => handleCopyNodeFrom('travel', id)}
                                        />
                                    </div>
                                </div>
                                <TravelBreakdownGroups
                                    travel={localNodeSet.travel}
                                    rates={rates}
                                    installDays={localNodeSet.install.days}
                                    laborLines={getInstallLaborLines(localNodeSet.install)}
                                    disabled={travelInputsLocked}
                                    formatCurrency={formatCurrency}
                                    onTravelChange={handleTravelPatch}
                                    onBlur={handleBlur}
                                    compareFields={compareMarks?.fields}
                                />
                            </div>
                        )}

                        {/* Legacy per-structure freight (still in structure total; editor hidden on new custom proposals). */}
                        {!kitMode && hideStructureFreightAndOverhead && (
                            <div className="detail-section detail-section-wide detail-section-legacy-freight">
                                <div className="detail-section-header">
                                    <div className="detail-section-header-title">
                                        <Truck size={16} />
                                        <h3>Freight</h3>
                                        <span className="detail-section-total">{formatCurrency(localNodeSet.freight.total)}</span>
                                    </div>
                                    <div className="detail-section-header-actions">
                                        {renderSectionMetrics(localNodeSet.freight.total, 'freight', 'Freight')}
                                        <CopyNodeFromSelect
                                            structures={allNodeSets}
                                            currentId={localNodeSet.id}
                                            disabled={disabled}
                                            onPick={(id) => handleCopyNodeFrom('freight', id)}
                                        />
                                    </div>
                                </div>
                                <p className="detail-section-hint">
                                    Included in the structure total above. New proposals use Project → Global Costs for freight;
                                    older structures may still carry freight here — use Copy to match another structure.
                                </p>
                            </div>
                        )}

                        {/* Freight — after Travel; hidden in kit mode and when freight is on Global Costs */}
                        {!kitMode && !hideStructureFreightAndOverhead && (
                            <div className="detail-section">
                                    <div className="detail-section-header">
                                        <Truck size={18} />
                                        <h3>Freight</h3>
                                        {renderSectionMetrics(localNodeSet.freight.total, 'freight', 'Freight')}
                                        <span className="detail-section-total">{formatCurrency(localNodeSet.freight.total)}</span>
                                    </div>

                                    {/* To Production Facility */}
                                    <div className="freight-leg">
                                        <div className="freight-leg-header">
                                            <span>To Production Facility</span>
                                            <label className="checkbox-label">
                                                <input
                                                    type="checkbox"
                                                    checked={freightFlatFeeMode.toProduction}
                                                    onChange={() => handleFreightModeToggle('toProduction')}
                                                />
                                                Flat Fee
                                            </label>
                                        </div>
                                        {freightFlatFeeMode.toProduction ? (
                                            <div className="detail-input-group">
                                                <label>Flat Fee ($)</label>
                                                <input
                                                    type="number"
                                                    value={localNodeSet.freight.flatFeeToAspers}
                                                    onChange={(e) => handleFreightChange('flatFeeToAspers', parseFloat(e.target.value) || 0)}
                                                    onBlur={handleBlur}
                                                />
                                            </div>
                                        ) : (
                                            <div className="detail-input-group">
                                                <label>Miles</label>
                                                <input
                                                    type="number"
                                                    value={localNodeSet.freight.milesToAspers}
                                                    onChange={(e) => handleFreightChange('milesToAspers', parseFloat(e.target.value) || 0)}
                                                    onBlur={handleBlur}
                                                />
                                            </div>
                                        )}
                                    </div>

                                    {/* To Job Site */}
                                    <div className="freight-leg">
                                        <div className="freight-leg-header">
                                            <span>To Job Site</span>
                                            <label className="checkbox-label">
                                                <input
                                                    type="checkbox"
                                                    checked={freightFlatFeeMode.toJobSite}
                                                    onChange={() => handleFreightModeToggle('toJobSite')}
                                                />
                                                Flat Fee
                                            </label>
                                        </div>
                                        {freightFlatFeeMode.toJobSite ? (
                                            <div className="detail-input-group">
                                                <label>Flat Fee ($)</label>
                                                <input
                                                    type="number"
                                                    value={localNodeSet.freight.flatFeeToJobSite}
                                                    onChange={(e) => handleFreightChange('flatFeeToJobSite', parseFloat(e.target.value) || 0)}
                                                    onBlur={handleBlur}
                                                />
                                            </div>
                                        ) : (
                                            <div className="detail-input-group">
                                                <label>Miles</label>
                                                <input
                                                    type="number"
                                                    value={localNodeSet.freight.milesToJobSite}
                                                    onChange={(e) => handleFreightChange('milesToJobSite', parseFloat(e.target.value) || 0)}
                                                    onBlur={handleBlur}
                                                />
                                            </div>
                                        )}
                                    </div>
                            </div>
                        )}
                        {/* Overhead Section - Hidden in Kit Mode and on new custom (global costing) proposals */}
                        {!kitMode && !hideStructureFreightAndOverhead && (
                            <div
                                className={`detail-section ${structureOverheadLockedByProjectOverhead ? 'detail-section-project-overhead-locked' : ''}`}
                            >
                                <div className="detail-section-header">
                                    <DollarSign size={18} />
                                    <h3>Overhead</h3>
                                    {renderSectionMetrics(localNodeSet.overhead.total, 'overhead', 'Overhead')}
                                    <span className="detail-section-total">{formatCurrency(localNodeSet.overhead.total)}</span>
                                </div>
                                {structureOverheadLockedByProjectOverhead && (
                                    <p className="detail-section-lock-hint">
                                        Project overhead (% of total) is on under Global Project Costs. Turn it off there to edit per-structure overhead days.
                                    </p>
                                )}
                                <div className="detail-input-group">
                                    <label>Days (${rates.overheadDailyRate}/day)</label>
                                    <input
                                        type="number"
                                        step="0.5"
                                        value={localNodeSet.overhead.days}
                                        onChange={(e) => handleOverheadChange('days', parseFloat(e.target.value) || 0)}
                                        onBlur={handleBlur}
                                        disabled={structureOverheadLockedByProjectOverhead}
                                    />
                                </div>
                            </div>
                        )}

                    </div>
                    <ProposalStructurePresenceLayer cursors={structurePresence.fresh} />
                    </div>
                </div>

                {/* Footer */}
                <div className="detail-modal-footer">
                    <div className="footer-left">
                        <button className="detail-btn danger" onClick={handleDelete}>
                            <Trash2 size={16} />
                            Delete Structure
                        </button>
                    </div>


                    <div className="structure-total-display">
                        <span className="structure-total-label">
                            {showInternalTotalFooter
                                ? 'Client total (raw, excl. install & travel)'
                                : 'Structure total'}
                        </span>
                        <span className="structure-total-value">
                            {formatCurrency(displayedStructureTotal)}
                        </span>
                        {showInternalTotalFooter && (
                            <span className="structure-total-sub">
                                Internal raw (all nodes incl. install {formatCurrency(localNodeSet.install.total)} + travel{' '}
                                {formatCurrency(localNodeSet.travel.total)}): {formatCurrency(structureTotal)}
                            </span>
                        )}
                    </div>
                    <div className="footer-right">
                        <button className="detail-btn primary" onClick={handleClose}>
                            Done
                        </button>
                    </div>
                </div>
            </div>
        </div>
        {quickAddTimberItem && (
            <QuickAddTimberDialog
                item={quickAddTimberItem}
                timberDatabase={timberDatabase}
                onClose={() => setQuickAddTimberItem(null)}
            />
        )}

        {!disabled && projectId && (
            <BoardSnapshotPicker
                open={snapshotPickerOpen}
                projectId={projectId}
                importing={snapshotImporting}
                importProgress={snapshotImportProgress}
                onClose={() => {
                    if (!snapshotImporting) setSnapshotPickerOpen(false);
                }}
                onPickPdfPages={choices => void handlePickPdfPages(choices)}
                onPickModel={handlePickModel}
            />
        )}

        {htmlCaptureDoc && (
            <HtmlViewerModal
                url={htmlCaptureDoc.url}
                fetchUrl={htmlCaptureDoc.fetchUrl}
                title={htmlCaptureDoc.title}
                versionLabel={htmlCaptureDoc.versionLabel}
                captureMode
                onCapture={file => void handleHtmlCapture(file)}
                onClose={() => setHtmlCaptureDoc(null)}
            />
        )}
        </>
    );
};

export default StructureDetailModal;
